import { API_ORIGIN, apiUrl } from "./apiOrigin";
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { displayName, useMaskNames, MaskToggleButton } from "./nameMask";
const API = apiUrl("");

/**
 * Holiday rota — one tab per branch, month or week at a time.
 *
 * Each day cell shows every member of staff with a state: working, on leave, or
 * absent. Those states come from three sources and they do not agree with each
 * other by design, so the source is always shown:
 *
 *   clocked    an actual clocking exists — the strongest evidence there is
 *   leave      an approved or pending leave_requests day
 *   absence    an absences day, with its reason
 *   assumed    nothing recorded at all
 *
 * That last one matters. Where no clocking exists we assume the person worked
 * rather than greying them out: a clock terminal that was down for a day would
 * otherwise wipe the whole team off the rota, and a rota nobody trusts is worse
 * than no rota. It is marked "assumed" so the difference is visible.
 *
 * DAY HISTORY
 * -----------
 * Expanding a cell lists everybody on it and the day's change history from
 * holiday_rota_logs. Holiday and absence can NOT be cancelled from here — the
 * ✕ that did so was too easy to hit, and cancellation is managed elsewhere.
 * (The server's /api/holidayRota/removeDay route still exists; nothing here
 * calls it.)
 *
 * COVER TOLERANCE (v3)
 * --------------------
 * Two ways to judge whether a day is covered:
 *
 *   %          the original rule: each role's available share of its
 *              scheduled hours/people against one "acceptable cover" figure.
 *   thresholds a Min and Opt per branch per role, in STAFF or HOURS, set in
 *              the "Staff thresholds" grid. Below Min is red, from Min up to
 *              and including Opt is green, above Opt is amber.
 *
 * Thresholds are used for a branch only when its row is complete (every role
 * at that branch has Min and Opt, and Min <= Opt). Otherwise the % rule
 * stays in charge, and the status line above the rota says which is in use
 * and why. Requires holiday_rota_routes v3 (/api/holidayRota/thresholds/*).
 */

/** Colours for a judged role or day: short (red), ok (green), over (amber). */
const STATUS = {
  short: {
    stripe: "#CF1322",
    text: "#CF1322",
    row: "#FFCCC7",
    tint: "#FFF1F0",
    word: "Short"
  },
  ok: {
    stripe: "#389E0D",
    text: "#15803d",
    row: "#F6FFED",
    tint: "#F6FFED",
    word: "OK"
  },
  over: {
    stripe: "#d97706",
    text: "#b45309",
    row: "#FFE58F",
    tint: "#FFF7E6",
    word: "Over"
  }
};
const isNum = v => typeof v === "number" && Number.isFinite(v);
const fmtNum = (v, mode) => v == null ? "—" : mode === "hours" ? `${+(+v).toFixed(2)}h` : String(v);
const LEAVE_COLOURS = {
  FULL_DAY: "#3b82f6",
  HALF_DAY_MORNING: "#60a5fa",
  HALF_DAY_AFTERNOON: "#91D5FF",
  CUSTOM_HOURS: "#0958D9"
};
const ABSENCE_COLOURS = {
  SICK_LEAVE: "#CF1322",
  UNAUTHORISED: "#b45309",
  EMERGENCY: "#ea580c",
  OTHER: "#5D5D5D"
};
// Role name colours. Deliberately NO reds, oranges, ambers or greens: those
// mean short / over / OK on this page, and a role printed in orange-red read
// as "short" on every day whatever its cover.
const ROLE_PALETTE = ["#0958D9", "#531DAB", "#0e7490", "#4338ca", "#434343", "#7e22ce", "#0369a1", "#1e40af", "#531DAB", "#155e75"];

/** Role names are compared trimmed and case-insensitive. */
const normRole = s => String(s || "").trim().toLowerCase();
/** Roles hidden from the rota until someone ticks them. */
const DEFAULT_HIDDEN_ROLES = ["group manager"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const pretty = s => (s || "").toString().replace(/_/g, " ").toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
const iso = d => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
};
const isoUTC = d => {
  const x = new Date(d);
  return `${x.getUTCFullYear()}-${String(x.getUTCMonth() + 1).padStart(2, "0")}-${String(x.getUTCDate()).padStart(2, "0")}`;
};

/** "HH:MM" -> minutes past midnight. */
const hhmm = s => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(s || "").trim());
  return m ? +m[1] * 60 + +m[2] : 0;
};

/**
 * The agreed pattern for one person on one date.
 *
 * v44 — two sources, as the server sends them (staff_schedule.js):
 *
 *   week set   staffs.weeklySchedule: Week A / Week B, alternating every week —
 *              the week number counted from Monday 5 January 1970, even = A,
 *              odd = B. Only days WITH shifts are sent, so for these staff a
 *              missing day is a rostered day off. Split shifts arrive in
 *              `shifts`, and `paidMins` is the paid time (shifts less break).
 *   occurrence the legacy staff_times rows: the Nth of that weekday in the
 *              month, falling back to occurrence 1 rather than treating a gap
 *              as a day off.
 *
 * start == end (or no row, for Week A/B staff) means not rostered. That is a
 * real day off, and it is not the same thing as holiday.
 */
const abWeekOfDate = date => {
  const n = Math.floor((Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) - Date.UTC(1970, 0, 5)) / (7 * 86400000));
  return (n % 2 + 2) % 2 === 0 ? "A" : "B";
};
const patternFor = (times, staffId, date) => {
  if (!times || times.length === 0) return null;
  const weekday = date.getDay(); // 0 = Sunday
  const mine = times.filter(t => t.staffId === staffId);
  if (mine.length === 0) return null; // no pattern on file

  if (mine.some(t => t.week)) {
    const week = abWeekOfDate(date);
    const row = mine.find(t => t.week === week && t.weekday === weekday);
    if (!row) return {
      rostered: false,
      hours: 0,
      row: null,
      week
    };
    const hours = Number.isFinite(row.paidMins) && row.paidMins !== null ? row.paidMins / 60 : Math.max(0, hhmm(row.end) - hhmm(row.start) - (row.breakMins || 0)) / 60;
    return {
      rostered: hours > 0,
      hours,
      row,
      week
    };
  }
  const occurrence = Math.min(Math.ceil(date.getDate() / 7), 5);
  const rows = mine.filter(t => t.weekday === weekday);
  if (rows.length === 0) return null;
  const row = rows.find(t => t.occurrence === occurrence) || rows[0];
  const startM = hhmm(row.start);
  const endM = hhmm(row.end);
  if (endM <= startM) return {
    rostered: false,
    hours: 0,
    row
  };

  // paid breaks stay in the contracted total; unpaid ones come out
  const mins = endM - startM - (row.breaksPaid ? 0 : row.breakMins || 0);
  return {
    rostered: true,
    hours: Math.max(mins, 0) / 60,
    row
  };
};
const hoursBetween = (a, b) => {
  if (!a || !b) return 0;
  const h = (new Date(b) - new Date(a)) / 3600000;
  return h > 0 && h < 24 ? h : 0;
};

/**
 * Compose the rota for every day from..to (inclusive, local dates).
 * dayKey -> [{staff, state, source, label, detail, planned, hours, ...}]
 *
 * Precedence: absence beats leave beats clocking. Somebody off sick on a day
 * they had booked off is still off sick, and that is the fact a manager needs.
 * Pure, so the request review can compose days outside the window on screen.
 */
function composeDays(data, from, to) {
  if (!data) return {};
  const out = {};
  const put = (key, entry) => {
    if (!out[key]) out[key] = [];
    out[key].push(entry);
  };
  const leaveIdx = new Map();
  for (const l of data.leave) {
    // Only approved and pending requests take anyone off the rota. A
    // rejected (or withdrawn) request is history, not leave.
    if (l.status && !["APPROVED", "PENDING"].includes(l.status)) continue;
    for (const d of l.days) {
      const k = `${isoUTC(d.date)}|${l.staffId}`;
      leaveIdx.set(k, {
        ...d,
        status: l.status,
        reason: l.reason,
        id: l.id
      });
    }
  }
  const absIdx = new Map();
  for (const a of data.absences) {
    for (const d of a.days) {
      const k = `${isoUTC(d.date)}|${a.staffId}`;
      absIdx.set(k, {
        ...d,
        notes: a.notes,
        id: a.id
      });
    }
  }
  const clockIdx = new Map();
  for (const a of data.attendance) {
    const k = `${isoUTC(a.clockIn)}|${a.staffId}`;
    const prev = clockIdx.get(k);
    if (!prev) clockIdx.set(k, a);
  }
  const cur = new Date(from);
  while (cur <= to) {
    const key = iso(cur);
    for (const s of data.staff) {
      const kk = `${key}|${s.id}`;
      const abs = absIdx.get(kk);
      const lv = leaveIdx.get(kk);
      const ck = clockIdx.get(kk);
      const pat = patternFor(data.times, s.id, cur);
      const planned = pat ? pat.hours : 7.5; // no pattern on file: assume a full day

      // not rostered today. Distinct from holiday, and worth keeping out of
      // the way rather than pretending it is an absence.
      if (pat && !pat.rostered && !abs && !lv && !ck) {
        put(key, {
          staff: s,
          state: "off",
          source: "pattern",
          label: "Not scheduled",
          detail: "No agreed hours for this day",
          colour: "#D9D9D9",
          planned: 0,
          hours: 0
        });
        continue;
      }
      if (abs) {
        put(key, {
          staff: s,
          state: "absent",
          source: "absence",
          label: pretty(abs.absenceType),
          detail: abs.notes || null,
          colour: ABSENCE_COLOURS[abs.absenceType] || ABSENCE_COLOURS.OTHER,
          planned,
          hours: 0,
          // what the ✕ in the expanded cell needs to act on
          recordKind: "absence",
          recordId: abs.id,
          dateKey: key
        });
      } else if (lv) {
        const half = (lv.leaveType || "").startsWith("HALF_DAY");
        put(key, {
          staff: s,
          state: "holiday",
          source: "leave",
          label: pretty(lv.leaveType),
          detail: lv.status === "PENDING" ? "Request not yet approved" : lv.reason,
          pending: lv.status === "PENDING",
          colour: LEAVE_COLOURS[lv.leaveType] || LEAVE_COLOURS.FULL_DAY,
          planned,
          // what is left of the agreed day once the leave is taken out
          hours: lv.leaveType === "CUSTOM_HOURS" ? Math.max(0, planned - hoursBetween(lv.startTime, lv.endTime)) : half ? planned / 2 : 0,
          recordKind: "leave",
          recordId: lv.id,
          dateKey: key,
          leaveType: lv.leaveType,
          leaveStart: lv.startTime,
          leaveEnd: lv.endTime
        });
      } else if (ck) {
        put(key, {
          staff: s,
          state: "working",
          source: "clocked",
          label: "Clocked in",
          detail: ck.clockOut ? `${new Date(ck.clockIn).toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit"
          })}` + ` – ${new Date(ck.clockOut).toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit"
          })}` : "still clocked in",
          colour: "#389E0D",
          planned,
          hours: ck.clockOut ? hoursBetween(ck.clockIn, ck.clockOut) : planned
        });
      } else {
        put(key, {
          staff: s,
          state: "working",
          source: "assumed",
          label: "Assumed working",
          detail: "No clocking recorded — treated as working",
          colour: "#8C8C8C",
          planned,
          hours: planned
        });
      }
    }
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}
const HolidayRota = () => {
  const masked = useMaskNames();
  const [branches, setBranches] = useState([]);
  const [activeBranch, setActiveBranch] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cursor, setCursor] = useState(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
  });
  const [view, setView] = useState("month"); // month | week | year
  const [dayDetail, setDayDetail] = useState(null); // dayKey of the open dialog
  // The cover a branch is willing to run on. Below it the cell goes red.
  const [target, setTarget] = useState(80);
  const [basis, setBasis] = useState("auto"); // auto | hours | days
  // "Filter on": roles that are unticked are taken out of the rota entirely —
  // not shown, not counted, not judged. Stored as normalised names, remembered
  // per browser; Group Manager starts unticked.
  const [hiddenRoles, setHiddenRoles] = useState(() => {
    try {
      const v = JSON.parse(localStorage.getItem("holidayRota.hiddenRoles") || "null");
      if (Array.isArray(v)) return new Set(v.map(normRole));
    } catch (_) {/* ignore */}
    return new Set(DEFAULT_HIDDEN_ROLES);
  });
  const isHidden = useCallback(role => hiddenRoles.has(normRole(role)), [hiddenRoles]);
  const toggleRole = role => setHiddenRoles(prev => {
    const next = new Set(prev);
    const k = normRole(role);
    if (next.has(k)) next.delete(k);else next.add(k);
    try {
      localStorage.setItem("holidayRota.hiddenRoles", JSON.stringify([...next]));
    } catch (_) {/* ignore */}
    return next;
  });
  // "How to use this page" popup
  const [showKey, setShowKey] = useState(false);
  const [panelTab, setPanelTab] = useState("outstanding"); // outstanding | completed | allowance
  const [allowance, setAllowance] = useState(null);
  const [allowSort, setAllowSort] = useState({
    by: "name",
    dir: 1
  });
  const [reqDetail, setReqDetail] = useState(null); // the request being decided
  const [actionResult, setActionResult] = useState(null);
  const [addOpen, setAddOpen] = useState(null); // 'leave' | 'absence' | null
  // Bumped after any write, to re-pull the window rather than patch state by
  // hand — the grid is derived from four collections and second-guessing what
  // a removal did to the derived figures is how they drift apart.
  const [reloadKey, setReloadKey] = useState(0);
  const [dayLog, setDayLog] = useState(null); // log rows for the open day
  // v3: the Min/Opt threshold grid, as /api/holidayRota/thresholds/get returns it
  const [thresholds, setThresholds] = useState(null);
  const [threshOpen, setThreshOpen] = useState(false);
  const [toleranceHelp, setToleranceHelp] = useState(false);
  const token = localStorage.getItem("authToken");
  const post = useCallback(async (path, body) => {
    const res = await fetch(`${API}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(body)
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }, [token]);
  useEffect(() => {
    (async () => {
      try {
        const d = await post("/api/holidayRota/branches", {});
        const list = d.branches || [];
        setBranches(list);
        if (list.length) setActiveBranch(list[0]);
      } catch (e) {
        setError(`Could not load branches: ${e.message}`);
      }
    })();
  }, [post]);

  /* ---------------------------------------------- cover thresholds (v3) */

  const loadThresholds = useCallback(async () => {
    try {
      setThresholds(await post("/api/holidayRota/thresholds/get", {}));
    } catch (e) {
      // Not fatal: the rota simply carries on with the % rule.
      setThresholds({
        error: e.message
      });
    }
  }, [post]);
  useEffect(() => {
    loadThresholds();
  }, [loadThresholds]);

  /**
   * Which rule judges the ACTIVE branch, and why.
   *
   * method 'staff' | 'hours' only when that branch's row in the selected set
   * is complete: every role defined at the branch has both Min and Opt, and
   * Min is not above Opt. Anything less and it is 'percent' — a grid with
   * holes in it would leave some roles judged and others silently not.
   */
  const tolerance = useMemo(() => {
    const t = thresholds;
    if (!t || t.error || !activeBranch) {
      return {
        method: "percent",
        reason: t?.error ? "unavailable" : "loading",
        mode: t?.mode || "staff",
        required: [],
        row: {},
        missing: [],
        invalid: []
      };
    }
    const mode = t.mode === "hours" ? "hours" : "staff";
    // Roles filtered off the rota are not shown or judged, so they need no
    // thresholds either.
    const required = (t.branchRoles?.[activeBranch.dbName] || []).filter(r => !isHidden(r));
    const row = t.cells?.[mode]?.[activeBranch.dbName] || {};
    const missing = [];
    const invalid = [];
    let any = false;
    for (const role of required) {
      const c = row[role];
      const hasMin = isNum(c?.min),
        hasOpt = isNum(c?.opt);
      if (hasMin || hasOpt) any = true;
      if (!hasMin || !hasOpt) missing.push(role);else if (c.min > c.opt) invalid.push(role);
    }
    const complete = required.length > 0 && missing.length === 0 && invalid.length === 0;

    // how much of the whole organisation's grid is done, for the status line
    let branchesComplete = 0;
    for (const b of t.branches || []) {
      const req = (t.branchRoles?.[b.dbName] || []).filter(x => !isHidden(x));
      const r = t.cells?.[mode]?.[b.dbName] || {};
      if (req.length && req.every(role => isNum(r[role]?.min) && isNum(r[role]?.opt) && r[role].min <= r[role].opt)) branchesComplete += 1;
    }
    return {
      method: complete ? mode : "percent",
      reason: complete ? "complete" : any ? "incomplete" : "empty",
      mode,
      required,
      row,
      missing,
      invalid,
      complete,
      branchesComplete,
      branchCount: (t.branches || []).length
    };
  }, [thresholds, activeBranch, isHidden]);

  /** Fold a saved cell (or a mode switch) into local state so the rota re-colours at once. */
  const applyThresholdChange = useCallback(change => {
    setThresholds(t => {
      if (!t || t.error) return t;
      if (change.mode && !change.cells) return {
        ...t,
        mode: change.mode
      };
      const cells = {
        staff: {
          ...(t.cells?.staff || {})
        },
        hours: {
          ...(t.cells?.hours || {})
        }
      };
      for (const c of change.cells || []) {
        const set = cells[change.set];
        set[c.dbName] = {
          ...(set[c.dbName] || {})
        };
        set[c.dbName][c.role] = {
          ...(set[c.dbName][c.role] || {
            min: null,
            opt: null
          })
        };
        if ("min" in c) set[c.dbName][c.role].min = c.min;
        if ("opt" in c) set[c.dbName][c.role].opt = c.opt;
      }
      return {
        ...t,
        cells
      };
    });
  }, []);

  /* --------------------------------------------------------- window dates */

  const windowRange = useMemo(() => {
    if (view === "year") {
      return {
        from: new Date(cursor.getFullYear(), 0, 1),
        to: new Date(cursor.getFullYear(), 11, 31)
      };
    }
    if (view === "week") {
      const s = new Date(cursor);
      const dow = (s.getDay() + 6) % 7; // Monday = 0
      s.setDate(s.getDate() - dow);
      const e = new Date(s);
      e.setDate(s.getDate() + 6);
      return {
        from: s,
        to: e
      };
    }
    return {
      from: new Date(cursor.getFullYear(), cursor.getMonth(), 1),
      to: new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0)
    };
  }, [cursor, view]);
  useEffect(() => {
    if (!activeBranch) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const d = await post("/api/holidayRota/data", {
          scheduleApi: 2,
          // v44: send the real Week A / Week B rows
          dbName: activeBranch.dbName,
          pharmacyId: activeBranch.pharmacyId,
          from: iso(windowRange.from),
          to: iso(windowRange.to)
        });
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setError(`Could not load rota: ${e.message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeBranch, windowRange, post, reloadKey]);

  /* --------------------------------------- every request for the branch */

  // The side panel lists ALL of the branch's requests, not just those that
  // overlap the window being looked at. null = not loaded / endpoint missing,
  // in which case the panel falls back to the window's own leave.
  const [allRequests, setAllRequests] = useState(null);
  useEffect(() => {
    if (!activeBranch) return;
    let cancelled = false;
    setAllRequests(null);
    (async () => {
      try {
        const d = await post("/api/holidayRota/requests", {
          dbName: activeBranch.dbName,
          pharmacyId: activeBranch.pharmacyId
        });
        if (!cancelled) setAllRequests(d.requests || []);
      } catch (_) {
        if (!cancelled) setAllRequests(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeBranch, post, reloadKey]);

  /* ------------------------------------------- removing a leave/absence day */

  /** The day's change history, shown at the foot of the expanded cell. */
  const loadDayLog = useCallback(async key => {
    if (!activeBranch || !key) {
      setDayLog(null);
      return;
    }
    try {
      const d = await post("/api/holidayRota/logs", {
        dbName: activeBranch.dbName,
        pharmacyId: activeBranch.pharmacyId,
        dateKey: key,
        limit: 25
      });
      setDayLog(d.logs || []);
    } catch (_) {
      setDayLog([]); // no history is not an error worth shouting about
    }
  }, [activeBranch, post]);
  useEffect(() => {
    if (dayDetail) loadDayLog(dayDetail);else setDayLog(null);
  }, [dayDetail, loadDayLog, reloadKey]);
  const loadAllowance = useCallback(async () => {
    if (!activeBranch) return;
    try {
      const d = await post("/api/holidayRota/allowance", {
        dbName: activeBranch.dbName,
        pharmacyId: activeBranch.pharmacyId,
        year: cursor.getFullYear()
      });
      setAllowance(d);
    } catch (e) {
      setError(`Could not load allowances: ${e.message}`);
    }
  }, [activeBranch, cursor, post]);
  useEffect(() => {
    if (panelTab === "allowance") loadAllowance();
  }, [panelTab, loadAllowance]);

  /* ------------------------------------------------- compose the day grid */

  const roleColour = useMemo(() => {
    const m = new Map();
    (data?.roles || []).forEach((r, i) => m.set(r.id, ROLE_PALETTE[i % ROLE_PALETTE.length]));
    return m;
  }, [data]);

  /**
   * dayKey -> [{staff, state, source, detail, hours}]
   *
   * Precedence: absence beats leave beats clocking. Somebody off sick on a day
   * they had booked off is still off sick, and that is the fact a manager needs.
   */
  const byDay = useMemo(() => composeDays(data, windowRange.from, windowRange.to), [data, windowRange]);

  // Week view lists everyone due in — working, on holiday or absent. People
  // not scheduled that day are left out; the day popup still shows them.
  const visible = e => e.state !== "off";

  // All of the branch's requests when the server provides them; otherwise
  // only those overlapping the window (older backend).
  const requestSource = allRequests || data?.leave || [];
  const firstDay = l => new Date(l.startDate || l.days?.[0]?.date || 0).getTime();
  // outstanding: soonest first — the next one to need a decision is on top
  const pending = useMemo(() => requestSource.filter(l => l.status === "PENDING").sort((a, b) => firstDay(a) - firstDay(b)), [requestSource] // eslint-disable-line react-hooks/exhaustive-deps
  );
  // A pending request whose LAST day is before today can no longer be
  // reviewed in any useful sense — the leave has either happened or not. It is
  // kept out of "Outstanding" (and its count) and listed separately, so it can
  // still be closed off for the record.
  const todayKey = iso(new Date());
  const lastDayKey = l => (l.days || []).map(d => isoUTC(d.date)).sort().pop() || "";
  const pendingLive = pending.filter(l => lastDayKey(l) >= todayKey);
  const pendingLapsed = pending.filter(l => lastDayKey(l) < todayKey).reverse(); // newest first
  const [showLapsed, setShowLapsed] = useState(false);

  // completed: most recent first
  const completed = useMemo(() => requestSource.filter(l => l.status !== "PENDING").sort((a, b) => firstDay(b) - firstDay(a)), [requestSource] // eslint-disable-line react-hooks/exhaustive-deps
  );

  /** "Mon 3 Nov – Fri 7 Nov 2026 · 5 days" for a request's days[] */
  const requestSpan = l => {
    const ds = (l.days || []).map(d => isoUTC(d.date)).sort();
    if (!ds.length) return "";
    const f = (k, yr) => {
      const x = new Date(k);
      return `${x.toLocaleDateString("en-GB", {
        weekday: "short"
      })} ${x.getDate()} ` + `${x.toLocaleDateString("en-GB", {
        month: "short"
      })}${yr ? ` ${x.getFullYear()}` : ""}`;
    };
    const span = ds.length === 1 ? f(ds[0], true) : `${f(ds[0])} – ${f(ds[ds.length - 1], true)}`;
    return `${span} · ${ds.length} day${ds.length === 1 ? "" : "s"}`;
  };
  const staffById = useMemo(() => new Map((data?.staff || []).map(s => [s.id, s])), [data]);
  const allowanceSorted = useMemo(() => {
    const rows = [...(allowance?.staff || [])];
    const {
      by,
      dir
    } = allowSort;
    rows.sort((a, b) => {
      if (by === "remaining") {
        const av = a.remainingHours ?? -Infinity;
        const bv = b.remainingHours ?? -Infinity;
        return (av - bv) * dir;
      }
      return a.name.localeCompare(b.name) * dir;
    });
    return rows;
  }, [allowance, allowSort]);

  /**
   * Decide a request, then tell the person.
   *
   * The message goes through /api/headoffice/staff-messages/send - the same
   * endpoint Communications and Single staff use - so it lands in their message
   * history with the usual audit trail rather than in a private side channel
   * nobody thinks to check.
   */
  const decideRequest = async (l, status) => {
    const s = staffById.get(l.staffId);
    try {
      await post("/api/holidayRota/actionRequest", {
        dbName: activeBranch.dbName,
        requestId: l.id,
        status
      });

      // fresh figures, so the message quotes what is actually left
      let remaining = null;
      try {
        const a = await post("/api/holidayRota/allowance", {
          dbName: activeBranch.dbName,
          year: cursor.getFullYear()
        });
        setAllowance(a);
        remaining = (a.staff || []).find(x => x.staffId === l.staffId)?.remainingHours ?? null;
      } catch (_) {/* the decision stands even if the figures do not load */}
      const days = l.days.map(d => isoUTC(d.date)).join(", ");
      const verdict = status === "APPROVED" ? "approved" : "not approved";
      const subject = `Holiday request ${verdict}`;
      const body = `Your holiday request for ${days} has been ${verdict}.\n\n` + (remaining != null ? `You have ${remaining.toFixed(1)} hours of holiday allowance remaining ` + `for ${cursor.getFullYear()}. Please arrange to take it before the end of the year.\n\n` : "") + `If you have any questions please speak to your manager.`;
      let emailed = false;
      let emailError = null;
      if (s?.email) {
        try {
          await post("/api/headoffice/staff-messages/send", {
            recipients: [{
              dbName: activeBranch.dbName,
              staffId: l.staffId
            }],
            subject,
            body,
            method: "email"
          });
          emailed = true;
        } catch (e) {
          emailError = e.message;
        }
      } else {
        emailError = "no email address on file";
      }
      setReqDetail(null);
      setActionResult({
        name: s?.name || "Unknown",
        status,
        days,
        remaining,
        emailed,
        emailError
      });
      setReloadKey(k => k + 1); // refetch the window and the request list
    } catch (e) {
      setError(`Could not update the request: ${e.message}`);
    }
  };

  /** Every staff member with allowance left, told what is outstanding. */
  const emailAllOutstanding = async () => {
    const rows = (allowance?.staff || []).filter(r => r.email && r.remainingHours != null && r.remainingHours > 0);
    if (rows.length === 0) {
      setActionResult({
        bulk: true,
        sent: 0,
        note: "Nobody has both an email address and hours remaining."
      });
      return;
    }
    let sent = 0;
    const failed = [];
    for (const r of rows) {
      try {
        await post("/api/headoffice/staff-messages/send", {
          recipients: [{
            dbName: activeBranch.dbName,
            staffId: r.staffId
          }],
          subject: `Your remaining holiday allowance for ${allowance.year}`,
          body: `Your holiday allowance for ${allowance.year} is ` + `${r.allowanceHours.toFixed(1)} hours.\n` + `You have taken ${r.takenHours.toFixed(1)} hours so far, ` + `leaving ${r.remainingHours.toFixed(1)} hours.\n\n` + (r.pendingHours > 0 ? `A further ${r.pendingHours.toFixed(1)} hours are requested but not yet approved.\n\n` : "") + `Holiday must be taken before the end of the year, so please book ` + `the remainder in with your manager in good time.`,
          method: "email"
        });
        sent += 1;
      } catch (e) {
        failed.push(`${r.name}: ${e.message}`);
      }
    }
    setActionResult({
      bulk: true,
      sent,
      failed
    });
  };

  /* ------------------------------------------------------------ rendering */

  const gridDays = useMemo(() => {
    const days = [];
    if (view === "week") {
      const c = new Date(windowRange.from);
      for (let i = 0; i < 7; i++) {
        days.push(new Date(c));
        c.setDate(c.getDate() + 1);
      }
      return days;
    }
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const lead = (first.getDay() + 6) % 7;
    const c = new Date(first);
    c.setDate(c.getDate() - lead);
    for (let i = 0; i < 42; i++) {
      days.push(new Date(c));
      c.setDate(c.getDate() + 1);
    }
    return days;
  }, [cursor, view, windowRange]);
  const move = delta => {
    const c = new Date(cursor);
    if (view === "week") c.setDate(c.getDate() + delta * 7);else if (view === "year") c.setFullYear(c.getFullYear() + delta);else c.setMonth(c.getMonth() + delta);
    setCursor(c);
  };

  // NB: index.css sets `button { color: white }` for the whole app, so every
  // button here must state its own colour or it renders white on white.
  // heading controls: big enough to read as the page's title
  const bigSelect = {
    fontSize: "1.15em",
    padding: "4px 6px",
    border: "1px solid #D9D9D9",
    borderRadius: 4,
    background: "#fff",
    color: "#434343",
    cursor: "pointer"
  };
  const stepBtn = {
    fontSize: "1.2em",
    lineHeight: 1,
    padding: "4px 10px",
    cursor: "pointer",
    border: "1px solid #D9D9D9",
    borderRadius: 4,
    background: "#fff",
    color: "#262626"
  };
  const btn = active => ({
    padding: "5px 10px",
    marginRight: 6,
    cursor: "pointer",
    border: "1px solid #D9D9D9",
    borderRadius: 4,
    background: active ? "#0958D9" : "#fff",
    color: active ? "#fff" : "#262626"
  });

  /**
   * One day, summarised by role.
   *
   * Cover is measured in hours where an agreed pattern exists, and in headcount
   * where it does not. Reporting a percentage of hours when nobody has a
   * pattern on file would be a number with nothing behind it, so the basis is
   * chosen from the data and shown alongside the figure.
   */
  const dayStats = useCallback(key => {
    // roles filtered off the rota are dropped before anything is counted
    const all = (byDay[key] || []).filter(e => !isHidden(e.staff.role || "No role"));
    const rostered = all.filter(e => e.state !== "off");
    const thresholdMode = tolerance.method !== "percent";
    const roles = new Map();
    for (const e of rostered) {
      const r = (e.staff.role || "").trim() || "No role";
      if (!roles.has(r)) {
        roles.set(r, {
          role: r,
          roleId: e.staff.roleId,
          scheduled: 0,
          working: 0,
          holiday: 0,
          absent: 0,
          plannedHours: 0,
          availableHours: 0
        });
      }
      const s = roles.get(r);
      s.scheduled += 1;
      if (e.state === "working") s.working += 1;
      if (e.state === "holiday") s.holiday += 1;
      if (e.state === "absent") s.absent += 1;
      s.plannedHours += e.planned ?? 0;
      s.availableHours += e.hours;
    }

    // With thresholds, a role nobody is rostered in is not "nothing to judge"
    // — no pharmacist on an open day is exactly what the grid exists to catch.
    // Closed days (nobody rostered at all) are still left unjudged.
    if (thresholdMode && rostered.length > 0) {
      const idByName = new Map((data?.roles || []).map(r => [(r.name || "").trim(), r.id]));
      for (const role of tolerance.required) {
        if (!roles.has(role)) {
          roles.set(role, {
            role,
            roleId: idByName.get(role) || null,
            scheduled: 0,
            working: 0,
            holiday: 0,
            absent: 0,
            plannedHours: 0,
            availableHours: 0
          });
        }
      }
    }
    const list = [...roles.values()].sort((a, b) => a.role.localeCompare(b.role));
    const plannedHours = list.reduce((t, r) => t + r.plannedHours, 0);
    const availableHours = list.reduce((t, r) => t + r.availableHours, 0);
    const scheduled = list.reduce((t, r) => t + r.scheduled, 0);
    const working = list.reduce((t, r) => t + r.working, 0);
    const useHours = basis === "hours" || basis === "auto" && plannedHours > 0;

    // Cover per role, then the WORST of them.
    //
    // Averaging every role into one figure hides the only thing that matters.
    // Eight drivers at 88% and three dispensers at 0% averages to a comfortable
    // 75%, which is why every day looked the same shade of red: the number was
    // dominated by headcount rather than by whether the branch can function.
    // The weakest role is the constraint, so that is what the day is judged on.
    for (const r of list) {
      r.coverage = useHours ? r.plannedHours > 0 ? r.availableHours / r.plannedHours * 100 : null : r.scheduled > 0 ? r.working / r.scheduled * 100 : null;

      // status: 'short' | 'ok' | 'over' | null (not judged)
      if (!thresholdMode) {
        r.status = r.coverage === null ? null : r.coverage < target ? "short" : "ok";
      } else {
        const c = tolerance.row[r.role];
        if (!c || !isNum(c.min) || !isNum(c.opt)) {
          r.status = null; // e.g. "No role" — not in the grid
        } else {
          // staff: people actually available (✓); hours: hours available
          const v = tolerance.method === "hours" ? r.availableHours : r.working;
          const eps = 1e-6; // summed hours are floats
          r.value = v;
          r.min = c.min;
          r.opt = c.opt;
          // below Min is short; Min and Opt themselves are both within range
          r.status = v < c.min - eps ? "short" : v <= c.opt + eps ? "ok" : "over";
        }
      }
    }
    const counted = list.filter(r => r.status !== null);
    let coverage = null;
    let weakest = null;
    for (const r of counted) {
      if (r.coverage !== null && (coverage === null || r.coverage < coverage)) {
        coverage = r.coverage;
        weakest = r.role;
      }
    }

    // The day takes its worst judged role: short beats over beats ok.
    let status = null;
    if (counted.length) {
      status = counted.some(r => r.status === "short") ? "short" : counted.some(r => r.status === "over") ? "over" : "ok";
    }
    const flagged = counted.filter(r => r.status === status && status !== "ok").map(r => r.role);
    return {
      roles: list,
      plannedHours,
      availableHours,
      scheduled,
      working,
      holiday: list.reduce((t, r) => t + r.holiday, 0),
      absent: list.reduce((t, r) => t + r.absent, 0),
      coverage,
      weakest,
      basis: useHours ? "hours" : "days",
      status,
      flagged,
      method: tolerance.method,
      hasAnyone: rostered.length > 0
    };
  }, [byDay, basis, isHidden, target, tolerance, data]);

  /** Background tint for a day or role status. */
  const coverTint = status => status ? STATUS[status].tint : "#fff";

  /** Short label for a day's judgement, used in titles. */
  const dayVerdict = st => {
    if (!st.status) return "nobody scheduled";
    if (st.method === "percent") {
      return `${st.coverage.toFixed(0)}% cover (${st.basis}) — ${st.status === "short" ? "below" : "meets"} ${target}%`;
    }
    const word = st.status === "short" ? "short" : st.status === "over" ? "over optimum" : "within Min–Opt";
    return `${word}${st.flagged.length ? `: ${st.flagged.join(", ")}` : ""} (${st.method} thresholds)`;
  };

  /** One outstanding request in the side panel. */
  const RequestRow = ({
    l,
    lapsed
  }) => {
    const s = staffById.get(l.staffId);
    return <div onClick={() => setReqDetail(l)} style={{
      padding: "8px 10px",
      borderBottom: "1px solid #eee",
      cursor: "pointer",
      opacity: lapsed ? 0.6 : 1
    }}>
        <div style={{
        fontWeight: "bold",
        fontSize: "0.9em"
      }}>
          {displayName(s?.name || "Unknown", masked)}
          {s?.role && <span style={{
          fontWeight: "normal",
          color: "#5D5D5D"
        }}> · {s.role}</span>}
        </div>
        <div style={{
        fontSize: "0.85em",
        color: "#5D5D5D"
      }}>{requestSpan(l)}</div>
        <div style={{
        fontSize: "0.8em",
        color: "#5D5D5D"
      }}>
          {pretty(l.days[0]?.leaveType)}{l.reason ? ` — ${l.reason}` : ""}
          <span style={{
          color: lapsed ? "#5D5D5D" : "#0958D9"
        }}>
            {lapsed ? " · dates passed — click to close off" : " · click to review"}
          </span>
        </div>
      </div>;
  };
  return <div style={{
    padding: "0 16px 16px"
  }}>
      <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 12
    }}>
        <div style={{
        display: "flex",
        alignItems: "baseline",
        gap: 14,
        flexWrap: "wrap"
      }}>
          <h2 style={{
          margin: 0
        }}>Holiday rota</h2>
          <button onClick={() => setShowKey(true)} style={{
          background: "none",
          border: "none",
          color: "#0958D9",
          cursor: "pointer",
          padding: 0,
          fontSize: "0.9em",
          textDecoration: "underline"
        }}>
            How to use this page
          </button>
        </div>
        <MaskToggleButton />
      </div>

      <p style={{
      fontSize: "0.9em",
      color: "#555",
      margin: "4px 0 0",
      maxWidth: 940
    }}>
        Who is in, who is off and why, by branch. Attendance is taken from actual
        clockings where they exist; where nothing was recorded the person is shown
        as <em>assumed working</em> rather than dropped, so a quiet clock terminal
        never empties the rota.
      </p>

      {showKey && <div onClick={() => setShowKey(false)} style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.4)",
      zIndex: 155,
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    }}>
          <div onClick={e => e.stopPropagation()} style={{
        width: 760,
        maxWidth: "94vw",
        maxHeight: "86vh",
        overflowY: "auto",
        background: "#fff",
        borderRadius: 8
      }}>
            <RotaKey onClose={() => setShowKey(false)} basis={basis} target={target} method={tolerance.method} onToleranceHelp={() => {
          setShowKey(false);
          setToleranceHelp(true);
        }} />
          </div>
        </div>}

      {/* controls — branch, period and view now live in the heading above the grid */}
      <div style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      flexWrap: "wrap",
      margin: "12px 0"
    }}>
        <span style={{
        marginLeft: 12,
        fontSize: "0.9em",
        opacity: tolerance.method === "percent" ? 1 : 0.45
      }} title={tolerance.method === "percent" ? "A day is shown red when the cover available falls below this." : "Not in use for this branch — its staff/hours thresholds are complete, so those decide the colours."}>
          Acceptable cover
          <input type="number" min="0" max="100" value={target} onChange={e => setTarget(Math.max(0, Math.min(100, +e.target.value || 0)))} style={{
          width: 58,
          padding: 4,
          margin: "0 4px"
        }} />%
          <select value={basis} onChange={e => setBasis(e.target.value)} style={{
          padding: 4,
          marginLeft: 4
        }}>
            <option value="auto">of hours where known</option>
            <option value="hours">of hours</option>
            <option value="days">of headcount</option>
          </select>
        </span>

        <span style={{
        fontSize: "0.85em",
        color: "#5D5D5D"
      }}>or</span>
        <button style={{
        ...btn(tolerance.method !== "percent"),
        marginRight: 0
      }} onClick={() => setThreshOpen(true)} disabled={!thresholds || !!thresholds.error} title="Set a minimum and optimum number of staff (or hours) for every branch and role">
          ⚙ Staff thresholds
        </button>

        {/* Filter on: an unticked role is taken out of the rota completely —
            not shown in any cell, not counted, never colours a day. */}
        {(data?.roles || []).length > 0 && <span style={{
        marginLeft: 12,
        fontSize: "0.85em"
      }} title="Untick a role to remove it from the rota altogether. Remembered on this browser.">
            Filter on:
            {data.roles.filter(r => (r.name || "").trim()).map(r => {
          const on = !isHidden(r.name);
          return <label key={r.id} style={{
            marginLeft: 6,
            cursor: "pointer",
            color: on ? roleColour.get(r.id) || "#434343" : "#8C8C8C"
          }}>
                  <input type="checkbox" checked={on} onChange={() => toggleRole(r.name)} />
                  {r.name}
                </label>;
        })}
          </span>}

        <span style={{
        marginLeft: "auto"
      }}>
          <button style={{
          ...btn(false),
          background: "#0958D9",
          color: "#fff"
        }} onClick={() => setAddOpen("leave")}>+ Holiday</button>
          <button style={{
          ...btn(false),
          background: "#b45309",
          color: "#fff"
        }} onClick={() => setAddOpen("absence")}>+ Absence</button>
        </span>
      </div>

      {/* which tolerance rule is judging this branch, and why */}
      <ToleranceStatus tolerance={tolerance} target={target} branchName={displayName(activeBranch?.name, masked)} onHelp={() => setToleranceHelp(true)} onConfigure={() => setThreshOpen(true)} canConfigure={!!thresholds && !thresholds.error} />

      {/* What is being shown — and the controls to change it, in one place:
          branch, period (step arrows either side, month/year pickers) and view. */}
      <div style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      flexWrap: "wrap",
      margin: "2px 0 8px"
    }}>
        {branches.length === 0 ? <span style={{
        color: "#666"
      }}>Loading branches…</span> : <select value={activeBranch?.dbName || ""} aria-label="Branch" onChange={e => setActiveBranch(branches.find(b => b.dbName === e.target.value) || null)} style={{
        ...bigSelect,
        fontWeight: "bold",
        color: "#262626",
        minWidth: 200
      }}>
            {branches.map(b => <option key={b.dbName} value={b.dbName}>{displayName(b.name, masked)}</option>)}
          </select>}

        <span style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4
      }}>
          <button style={stepBtn} onClick={() => move(-1)} title={view === "week" ? "Previous week" : view === "year" ? "Previous year" : "Previous month"}>‹</button>
          {view === "week" && <span style={{
          fontSize: "1.1em",
          color: "#434343",
          padding: "0 4px",
          whiteSpace: "nowrap"
        }}>
              {`Week of ${windowRange.from.toLocaleDateString("en-GB", {
            weekday: "short",
            day: "numeric",
            month: "short"
          })}` + ` – ${windowRange.to.toLocaleDateString("en-GB", {
            weekday: "short",
            day: "numeric",
            month: "short"
          })}`}
            </span>}
          {view !== "year" && <select value={cursor.getMonth()} aria-label="Month" style={bigSelect} onChange={e => setCursor(new Date(cursor.getFullYear(), +e.target.value, 1))}>
              {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
            </select>}
          <select value={cursor.getFullYear()} aria-label="Year" style={bigSelect} onChange={e => setCursor(new Date(+e.target.value, cursor.getMonth(), 1))}>
            {Array.from({
            length: 7
          }, (_, i) => new Date().getFullYear() - 3 + i).map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button style={stepBtn} onClick={() => move(1)} title={view === "week" ? "Next week" : view === "year" ? "Next year" : "Next month"}>›</button>
          <button style={{
          ...btn(false),
          marginLeft: 4,
          marginRight: 0
        }} onClick={() => setCursor(new Date())}>Today</button>
        </span>

        <span style={{
        display: "inline-flex",
        border: "1px solid #D9D9D9",
        borderRadius: 6,
        overflow: "hidden",
        marginLeft: 6
      }}>
          {["week", "month", "year"].map(v => <button key={v} onClick={() => setView(v)} style={{
          padding: "6px 14px",
          border: 0,
          cursor: "pointer",
          borderLeft: v === "week" ? 0 : "1px solid #D9D9D9",
          background: view === v ? "#0958D9" : "#fff",
          color: view === v ? "#fff" : "#262626",
          fontWeight: view === v ? "bold" : "normal"
        }}>
              {pretty(v)}
            </button>)}
        </span>
      </div>

      {error && <div style={{
      background: "#fff4f4",
      border: "1px solid #e0b4b4",
      padding: "8px 10px",
      marginBottom: 10
    }}>{error}</div>}

      <div style={{
      display: "flex",
      gap: 16,
      alignItems: "flex-start"
    }}>
        <div style={{
        flex: 1
      }}>
          {loading && <div style={{
          padding: 12
        }}>Loading rota…</div>}

          {!loading && data && view !== "year" && <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 1,
          background: "#F0F0F0",
          border: "1px solid #F0F0F0"
        }}>
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(d => <div key={d} style={{
            background: "#FAFAFA",
            padding: "6px 8px",
            fontWeight: "bold",
            fontSize: "0.85em"
          }}>{d}</div>)}
              {gridDays.map(d => {
            const key = iso(d);
            const inMonth = view === "week" || d.getMonth() === cursor.getMonth();
            const st = dayStats(key);
            const today = key === iso(new Date());
            return <div key={key} onClick={() => setDayDetail(key)} style={{
              // The day stays white; a stripe down the left says whether the
              // day as a whole is covered, and only the short roles are shaded.
              background: inMonth ? "#fff" : "#fafafa",
              boxShadow: !st.status ? "none" : `inset 5px 0 0 ${STATUS[st.status].stripe}`,
              minHeight: view === "week" ? 300 : 118,
              padding: "5px 5px 5px 10px",
              cursor: "pointer",
              outline: today ? "2px solid #0958D9" : "none",
              opacity: inMonth ? 1 : 0.45,
              overflow: "hidden"
            }}>
                    {/* A short or over day gets a solid bar across the top naming
                        the roles responsible, so it stands out from across the
                        room; a day that is fine keeps the quiet header. */}
                    {st.status === "short" || st.status === "over" ? <div title={dayVerdict(st)} style={{
                margin: "-5px -5px 4px -10px",
                padding: "3px 6px 3px 10px",
                background: st.status === "short" ? "#CF1322" : "#FA8C16",
                color: st.status === "short" ? "#fff" : "#422006",
                display: "flex",
                gap: 6,
                alignItems: "baseline",
                fontSize: "0.75em",
                lineHeight: 1.3
              }}>
                        <span style={{
                  fontWeight: "bold",
                  fontSize: "1.05em"
                }}>{d.getDate()}</span>
                        <span style={{
                  fontWeight: "bold",
                  whiteSpace: "nowrap"
                }}>
                          {st.method === "percent" && st.coverage !== null ? `${st.coverage.toFixed(0)}%` : STATUS[st.status].word}
                        </span>
                        {st.flagged.length > 0 && <span style={{
                  overflowWrap: "anywhere"
                }}>{st.flagged.join(", ")}</span>}
                      </div> : <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline"
              }}>
                        <span style={{
                  fontSize: "0.8em",
                  fontWeight: "bold",
                  color: "#5D5D5D"
                }}>
                          {d.getDate()}
                        </span>
                        {st.status === "ok" && <span style={{
                  fontSize: "0.72em",
                  fontWeight: "bold",
                  color: STATUS.ok.text
                }} title={dayVerdict(st)}>
                            {st.method === "percent" && st.coverage !== null ? `${st.coverage.toFixed(0)}%` : "OK"}
                          </span>}
                      </div>}

                    {/* roles, not names: a month cell has no room for a team
                        and the shape of the cover is what matters at a glance */}
                    {st.roles.length === 0 ? <div style={{
                fontSize: "0.7em",
                color: "#8C8C8C",
                marginTop: 4
              }}>
                        nobody scheduled
                      </div> : <table style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "0.68em",
                marginTop: 3
              }}>
                        <thead>
                          <tr style={{
                    color: "#5D5D5D"
                  }}>
                            <th style={{
                      textAlign: "left",
                      fontWeight: "normal"
                    }}>Role</th>
                            <th title="Scheduled — due to work this day from their working pattern">S</th>
                            <th title="Holiday — of those scheduled, on approved or pending leave">H</th>
                            <th title="Absent — of those scheduled, off sick or another recorded absence">A</th>
                            <th title="Available — actually working: clocked in, or assumed working where nothing was recorded">✓</th>
                            {st.method === "percent" && <th title="Cover — the share of this role’s scheduled hours (or people) that is actually available">%</th>}
                            {st.method === "staff" && <th title="Min / Opt staff set for this role in the thresholds grid">M/O</th>}
                            {st.method === "hours" && <th title="Hours available for this role, judged against its Min / Opt hours">Hrs</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {st.roles.map(r => {
                    const rs = r.status;
                    // only the rows that are a problem are shaded, so a bad
                    // day stands out and a good one stays quiet
                    const shade = rs === "short" || rs === "over" ? STATUS[rs].row : "transparent";
                    const why = st.method === "percent" ? `${r.role} is below the ${target}% acceptable cover` : `${r.role}: ${fmtNum(r.value, st.method)} available — Min ${fmtNum(r.min, st.method)}, Opt ${fmtNum(r.opt, st.method)} → ${STATUS[rs || "ok"].word}`;
                    return <tr key={r.role} title={st.method === "percent" ? rs === "short" ? why : undefined : rs ? why : undefined} style={{
                      background: shade
                    }}>
                              <td style={{
                        color: roleColour.get(r.roleId) || "#434343",
                        fontWeight: "bold",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: 70
                      }}>{r.role}</td>
                              <td style={{
                        textAlign: "center"
                      }}>{r.scheduled}</td>
                              <td style={{
                        textAlign: "center",
                        color: r.holiday ? "#0958D9" : "#D9D9D9"
                      }}>{r.holiday}</td>
                              <td style={{
                        textAlign: "center",
                        color: r.absent ? "#CF1322" : "#D9D9D9"
                      }}>{r.absent}</td>
                              <td style={{
                        textAlign: "center",
                        fontWeight: "bold"
                      }}>{r.working}</td>
                              {st.method === "percent" ? <td style={{
                        textAlign: "right",
                        color: r.coverage === null ? "#D9D9D9" : r.coverage < target ? "#CF1322" : "#15803d"
                      }}>
                                  {r.coverage === null ? "" : `${r.coverage.toFixed(0)}%`}
                                </td> : <td style={{
                        textAlign: "right",
                        whiteSpace: "nowrap",
                        color: rs ? STATUS[rs].text : "#D9D9D9"
                      }}>
                                  {r.min == null ? "" : st.method === "staff" ? `${r.min}/${r.opt}` : `${+r.availableHours.toFixed(1)}h`}
                                </td>}
                            </tr>;
                  })}
                        </tbody>
                      </table>}

                    {/* week view has the room to name people underneath */}
                    {view === "week" && st.hasAnyone && <div style={{
                marginTop: 6,
                borderTop: "1px solid #F0F0F0",
                paddingTop: 4
              }}>
                        {(byDay[key] || []).filter(e => visible(e) && !isHidden(e.staff.role || "No role")).map((e, i) => <div key={i} style={{
                  fontSize: "0.7em",
                  marginBottom: 2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  opacity: e.source === "assumed" ? 0.65 : 1,
                  fontStyle: e.pending ? "italic" : "normal"
                }}>
                            <span style={{
                    color: e.colour
                  }}>●</span>{" "}
                            <span style={{
                    color: roleColour.get(e.staff.roleId) || "#434343"
                  }}>
                              {displayName(e.staff.name, masked)}
                            </span>
                            {e.state !== "working" && <span style={{
                    color: "#5D5D5D"
                  }}> · {e.label}</span>}
                          </div>)}
                      </div>}
                  </div>;
          })}
            </div>}

          {!loading && data && view === "year" && <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 10
        }}>
              {MONTHS.map((m, mi) => {
            const days = [];
            const last = new Date(cursor.getFullYear(), mi + 1, 0).getDate();
            for (let dd = 1; dd <= last; dd++) days.push(new Date(cursor.getFullYear(), mi, dd));
            return <div key={m} style={{
              border: "1px solid #F0F0F0",
              padding: 6
            }}>
                    <div style={{
                fontWeight: "bold",
                fontSize: "0.85em",
                marginBottom: 4
              }}>{m}</div>
                    <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: 2
              }}>
                      {days.map(d => {
                  // same measure as the month view, so a red square here
                  // means exactly what a red cell means there
                  const st = dayStats(iso(d));
                  return <div key={iso(d)} title={`${iso(d)} — ${dayVerdict(st)}`} onClick={() => setDayDetail(iso(d))} style={{
                    height: 15,
                    fontSize: "0.6em",
                    textAlign: "center",
                    cursor: "pointer",
                    borderRadius: 2,
                    background: st.hasAnyone ? coverTint(st.status) : "#F5F5F5",
                    color: "#434343"
                  }}>{d.getDate()}</div>;
                })}
                    </div>
                  </div>;
          })}
            </div>}
        </div>

        {/* right-hand panel: requests, history and allowance */}
        <div style={{
        flex: "0 0 340px",
        border: "1px solid #ccc",
        maxHeight: "76vh",
        overflowY: "auto"
      }}>
          <div style={{
          display: "flex",
          borderBottom: "1px solid #ddd"
        }}>
            {[["outstanding", `Outstanding (${pendingLive.length})`], ["completed", `Completed (${completed.length})`], ["allowance", "Allowance"]].map(([k, label]) => <button key={k} onClick={() => setPanelTab(k)} style={{
            flex: 1,
            padding: "7px 4px",
            cursor: "pointer",
            fontSize: "0.82em",
            border: 0,
            borderBottom: panelTab === k ? "3px solid #0958D9" : "3px solid transparent",
            background: panelTab === k ? "#E6F7FF" : "#f6f8fa",
            color: panelTab === k ? "#0958D9" : "#434343",
            fontWeight: panelTab === k ? "bold" : "normal"
          }}>{label}</button>)}
          </div>

          {/* If the server can't list every request (older backend), say so
              plainly — otherwise "nothing outstanding" could be wrong. */}
          {allRequests === null && data && (panelTab === "outstanding" || panelTab === "completed") && <div style={{
          padding: "6px 10px",
          background: "#FFFBE6",
          borderBottom: "1px solid #FFE58F",
          color: "#AD4E00",
          fontSize: "0.8em"
        }}>
              Only showing requests in the {view} on screen — the server hasn&rsquo;t been updated to list
              every request for the branch yet.
            </div>}

          {panelTab === "outstanding" && <>
            {pendingLive.length === 0 && <div style={{
            padding: 12,
            color: "#666",
            fontSize: "0.9em"
          }}>
                {allRequests ? "No holiday requests are awaiting a decision for this branch." : "Nothing awaiting a decision in this window."}
              </div>}
            {pendingLive.map(l => <RequestRow key={l.id} l={l} />)}

            {pendingLapsed.length > 0 && <div style={{
            borderTop: "1px solid #F0F0F0"
          }}>
                <button onClick={() => setShowLapsed(v => !v)} style={{
              width: "100%",
              textAlign: "left",
              padding: "7px 10px",
              border: 0,
              background: "#FAFAFA",
              color: "#5D5D5D",
              cursor: "pointer",
              fontSize: "0.82em"
            }} title="Requests never approved or rejected whose dates have now passed">
                  {showLapsed ? "▾" : "▸"} Dates passed, never decided ({pendingLapsed.length})
                </button>
                {showLapsed && <>
                    <div style={{
                padding: "4px 10px 6px",
                fontSize: "0.78em",
                color: "#8C8C8C",
                background: "#FAFAFA"
              }}>
                      Too late to review, but still pending on record. Open one to approve or reject it
                      so the record and the allowance figures are right.
                    </div>
                    {pendingLapsed.map(l => <RequestRow key={l.id} l={l} lapsed />)}
                  </>}
              </div>}
          </>}

          {panelTab === "completed" && (completed.length === 0 ? <div style={{
          padding: 12,
          color: "#666",
          fontSize: "0.9em"
        }}>
                {allRequests ? "No decided holiday requests for this branch yet." : "No decided requests overlapping this period."}
              </div> : completed.map(l => {
          const s = staffById.get(l.staffId);
          const ok = l.status === "APPROVED";
          return <div key={l.id} style={{
            padding: "8px 10px",
            borderBottom: "1px solid #eee"
          }}>
                  <div style={{
              fontWeight: "bold",
              fontSize: "0.9em"
            }}>
                    {displayName(s?.name || "Unknown", masked)}
                  </div>
                  <div style={{
              fontSize: "0.85em",
              color: "#5D5D5D"
            }}>
                    {requestSpan(l)}
                  </div>
                  <span style={{
              fontSize: "0.78em",
              padding: "1px 6px",
              borderRadius: 3,
              background: ok ? "#F6FFED" : "#FFF1F0",
              color: ok ? "#15803d" : "#CF1322"
            }}>
                    {pretty(l.status)}
                  </span>
                </div>;
        }))}

          {panelTab === "allowance" && (!allowance ? <div style={{
          padding: 12,
          color: "#666",
          fontSize: "0.9em"
        }}>Loading…</div> : <>
                <div style={{
            padding: "8px 10px",
            borderBottom: "1px solid #eee",
            fontSize: "0.8em",
            color: "#5D5D5D"
          }}>
                  Leave year {allowance.year}. Where no allowance is on file it is
                  taken as {allowance.statutoryWeeks} weeks of the contracted week,
                  the statutory minimum — shown as <em>derived</em>.
                  <div style={{
              marginTop: 6
            }}>
                    <button onClick={emailAllOutstanding} style={{
                background: "#0958D9",
                color: "#fff",
                border: 0,
                borderRadius: 4,
                padding: "5px 9px",
                cursor: "pointer",
                fontSize: "0.95em"
              }}>
                      Email staff outstanding
                    </button>
                  </div>
                </div>

                <table style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "0.82em"
          }}>
                  <thead>
                    <tr style={{
                background: "#fafafa"
              }}>
                      <th style={{
                  textAlign: "left",
                  padding: 6,
                  cursor: "pointer"
                }} onClick={() => setAllowSort({
                  by: "name",
                  dir: allowSort.by === "name" ? -allowSort.dir : 1
                })}>
                        Staff {allowSort.by === "name" ? allowSort.dir > 0 ? "▲" : "▼" : ""}
                      </th>
                      <th style={{
                  textAlign: "right",
                  padding: 6
                }}>Allow</th>
                      <th style={{
                  textAlign: "right",
                  padding: 6
                }}>Taken</th>
                      <th style={{
                  textAlign: "right",
                  padding: 6,
                  cursor: "pointer"
                }} onClick={() => setAllowSort({
                  by: "remaining",
                  dir: allowSort.by === "remaining" ? -allowSort.dir : 1
                })}>
                        Left {allowSort.by === "remaining" ? allowSort.dir > 0 ? "▲" : "▼" : ""}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {allowanceSorted.map(r => <tr key={r.staffId} style={{
                borderTop: "1px solid #eee"
              }}>
                        <td style={{
                  padding: 6
                }}>
                          {displayName(r.name, masked)}
                          {!r.email && <span style={{
                    color: "#b45309",
                    fontSize: "0.9em"
                  }} title="No email address on file">
                              {" "}✉
                            </span>}
                          <div style={{
                    color: "#8C8C8C",
                    fontSize: "0.9em"
                  }}>
                            {r.role}
                            {r.allowanceSource === "derived" && " · derived"}
                            {r.allowanceSource === "unknown" && " · no pattern"}
                          </div>
                        </td>
                        <td style={{
                  padding: 6,
                  textAlign: "right"
                }}>
                          {r.allowanceHours == null ? "—" : r.allowanceHours.toFixed(1)}
                        </td>
                        <td style={{
                  padding: 6,
                  textAlign: "right"
                }}>
                          {r.takenHours.toFixed(1)}
                          {r.pendingHours > 0 && <div style={{
                    color: "#a05a00",
                    fontSize: "0.85em"
                  }} title="Requested but not yet approved">
                              +{r.pendingHours.toFixed(1)}
                            </div>}
                        </td>
                        <td style={{
                  padding: 6,
                  textAlign: "right",
                  fontWeight: "bold",
                  color: r.remainingHours == null ? "#8C8C8C" : r.remainingHours < 0 ? "#CF1322" : r.remainingHours === 0 ? "#15803d" : "#262626"
                }}>
                          {r.remainingHours == null ? "—" : r.remainingHours.toFixed(1)}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </>)}
        </div>
      </div>

      {/* day detail — opened by clicking a cell in any view */}
      {dayDetail && (() => {
      const st = dayStats(dayDetail);
      const entries = (byDay[dayDetail] || []).filter(e => !isHidden(e.staff.role || "No role"));
      const byRole = new Map();
      for (const e of entries) {
        const r = e.staff.role || "No role";
        if (!byRole.has(r)) byRole.set(r, []);
        byRole.get(r).push(e);
      }
      return <div onClick={() => setDayDetail(null)} style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100
      }}>
            <div onClick={ev => ev.stopPropagation()} style={{
          background: "#fff",
          borderRadius: 6,
          width: 560,
          maxWidth: "94vw",
          maxHeight: "86vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden"
        }}>
              {/* date, close and the day's verdict stay put; the people scroll */}
              <div style={{
            padding: "16px 16px 0",
            flex: "0 0 auto"
          }}>
              <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start"
            }}>
                <div>
                  <h3 style={{
                  margin: 0
                }}>
                    {new Date(dayDetail).toLocaleDateString("en-GB", {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                    year: "numeric"
                  })}
                  </h3>
                  <div style={{
                  fontSize: "0.85em",
                  color: "#5D5D5D"
                }}>
                    {displayName(activeBranch?.name, masked)}
                  </div>
                </div>
                <button onClick={() => setDayDetail(null)} style={{
                background: "#fff",
                color: "#262626",
                border: "1px solid #D9D9D9",
                borderRadius: 4,
                padding: "4px 10px"
              }}>Close</button>
              </div>

              {/* the cover figure, and what it is measured against */}
              <div style={{
              margin: "12px 0",
              padding: "8px 10px",
              borderRadius: 4,
              background: st.hasAnyone ? coverTint(st.status) : "#F5F5F5"
            }}>
                {!st.status ? <span style={{
                color: "#5D5D5D"
              }}>Nobody is scheduled on this day.</span> : st.method !== "percent" ? <>
                    <strong style={{
                  fontSize: "1.1em",
                  color: STATUS[st.status].text
                }}>
                      {st.status === "short" ? "Short" : st.status === "over" ? "Over optimum" : "Within Min–Opt"}
                    </strong>
                    <span style={{
                  color: "#5D5D5D"
                }}>
                      {" "}— judged on {st.method === "hours" ? "hours" : "staff numbers"} thresholds
                      {st.flagged.length > 0 && `: ${st.flagged.join(", ")}`}
                    </span>
                    <div style={{
                  fontSize: "0.85em",
                  color: "#5D5D5D",
                  marginTop: 2
                }}>
                      {st.working} of {st.scheduled} scheduled staff available
                      {` · ${st.availableHours.toFixed(1)}h of ${st.plannedHours.toFixed(1)}h`}
                      {st.holiday > 0 && ` · ${st.holiday} on holiday`}
                      {st.absent > 0 && ` · ${st.absent} absent`}
                    </div>
                  </> : <>
                    <strong style={{
                  fontSize: "1.1em"
                }}>{st.coverage.toFixed(0)}% cover</strong>
                    <span style={{
                  color: "#5D5D5D"
                }}>
                      {" "}against a {target}% target
                      {st.coverage < target ? " — short" : " — met"}
                      {st.status === "short" && st.flagged.length > 0 && `, short on ${st.flagged.join(", ")}`}
                    </span>
                    <div style={{
                  fontSize: "0.85em",
                  color: "#5D5D5D",
                  marginTop: 2
                }}>
                      {st.basis === "hours" ? `${st.availableHours.toFixed(1)}h available of ${st.plannedHours.toFixed(1)}h scheduled` : `${st.working} of ${st.scheduled} scheduled staff available`}
                      {st.holiday > 0 && ` · ${st.holiday} on holiday`}
                      {st.absent > 0 && ` · ${st.absent} absent`}
                    </div>
                  </>}
              </div>

              </div>

              <div style={{
            overflowY: "scroll",
            flex: "1 1 auto",
            minHeight: 0,
            padding: "0 16px 16px",
            borderTop: "1px solid #F0F0F0"
          }}>
              <div style={{
              height: 8
            }} />
              {[...byRole.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([role, list]) => {
              const rs = st.roles.find(r => r.role === role);
              return <div key={role} style={{
                marginBottom: 12
              }}>
                    <div style={{
                  fontWeight: "bold",
                  color: roleColour.get(list[0].staff.roleId) || "#434343",
                  borderBottom: "1px solid #F0F0F0",
                  paddingBottom: 3
                }}>
                      {role}
                      {rs && <span style={{
                    fontWeight: "normal",
                    color: "#5D5D5D",
                    fontSize: "0.85em"
                  }}>
                          {"  "}{rs.working}/{rs.scheduled} available
                          {rs.plannedHours > 0 && ` · ${rs.availableHours.toFixed(1)}h of ${rs.plannedHours.toFixed(1)}h`}
                        </span>}
                      {rs && st.method !== "percent" && rs.min != null && <span style={{
                    marginLeft: 8,
                    fontSize: "0.8em",
                    padding: "1px 6px",
                    borderRadius: 3,
                    fontWeight: "bold",
                    background: STATUS[rs.status].tint,
                    color: STATUS[rs.status].text
                  }}>
                          {STATUS[rs.status].word} · Min {fmtNum(rs.min, st.method)} / Opt {fmtNum(rs.opt, st.method)}
                        </span>}
                    </div>
                    {list.map((e, i) => <div key={i} style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "3px 0",
                  fontSize: "0.9em",
                  opacity: e.state === "off" ? 0.55 : 1
                }}>
                        <span>
                          {displayName(e.staff.name, masked)}
                          {e.detail && <span style={{
                      color: "#8C8C8C",
                      fontSize: "0.85em"
                    }}>
                              {" "}— {e.detail}
                            </span>}
                        </span>
                        <span style={{
                    whiteSpace: "nowrap",
                    marginLeft: 10
                  }}>
                          <span style={{
                      color: e.colour,
                      fontWeight: "bold"
                    }}>
                            {e.state === "working" ? "Working" : e.state === "holiday" ? "Holiday" : e.state === "absent" ? "Absent" : "Not scheduled"}
                          </span>
                          <span style={{
                      color: "#5D5D5D"
                    }}>
                            {" "}{e.hours > 0 ? `${e.hours.toFixed(1)}h` : "0h"}
                          </span>
                          <span style={{
                      color: "#D9D9D9",
                      fontSize: "0.85em"
                    }}>
                            {" "}({e.source})
                          </span>
                          {/* No remove button here: cancelling holiday or absence
                              from the day view was too easy to hit by accident,
                              and is managed elsewhere. */}
                        </span>
                      </div>)}
                  </div>;
            })}

              {/* What has been changed on this day, and by whom. Shown here
                  rather than behind another click: the question "who took
                  that holiday off?" is asked in exactly this dialogue. */}
              {dayLog && dayLog.length > 0 && <div style={{
              marginTop: 14,
              borderTop: "1px solid #F0F0F0",
              paddingTop: 10
            }}>
                  <div style={{
                fontWeight: "bold",
                fontSize: "0.9em",
                color: "#434343",
                marginBottom: 6
              }}>
                    Changes to this day
                  </div>
                  {dayLog.map((l, i) => <div key={i} style={{
                fontSize: "0.82em",
                color: "#5D5D5D",
                padding: "3px 0",
                borderBottom: i < dayLog.length - 1 ? "1px solid #F5F5F5" : "none"
              }}>
                      <div>{l.detail}</div>
                      {l.reason && <div style={{
                  color: "#5D5D5D",
                  fontStyle: "italic"
                }}>
                          Reason: {l.reason}
                        </div>}
                      <div style={{
                  color: "#8C8C8C",
                  fontSize: "0.9em"
                }}>
                        {new Date(l.ts).toLocaleString("en-GB")}
                        {l.by ? ` — ${l.by}` : ""}
                      </div>
                    </div>)}
                </div>}
              </div>
            </div>
          </div>;
    })()}

      {/* review a request: the request, each day for that role, the decision */}
      {reqDetail && <RequestReview request={reqDetail} staffById={staffById} post={post} branch={activeBranch} tolerance={tolerance} target={target} basis={basis} masked={masked} roleColour={roleColour} onDecide={decideRequest} onClose={() => setReqDetail(null)} />}

      {/* what happened, and what was sent */}
      {actionResult && <div onClick={() => setActionResult(null)} style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.35)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 120
    }}>
          <div onClick={ev => ev.stopPropagation()} style={{
        background: "#fff",
        borderRadius: 6,
        width: 440,
        padding: 18
      }}>
            {actionResult.bulk ? <>
                <h3 style={{
            marginTop: 0
          }}>Allowance reminders sent</h3>
                <p style={{
            fontSize: "0.95em"
          }}>
                  {actionResult.sent} message{actionResult.sent === 1 ? "" : "s"} sent
                  and recorded in each staff member&rsquo;s messages.
                </p>
                {actionResult.note && <p style={{
            color: "#a05a00",
            fontSize: "0.9em"
          }}>{actionResult.note}</p>}
                {actionResult.failed?.length > 0 && <div style={{
            color: "#CF1322",
            fontSize: "0.85em"
          }}>
                    <strong>Not sent:</strong>
                    <ul style={{
              margin: "4px 0 0 18px"
            }}>
                      {actionResult.failed.map((f, i) => <li key={i}>{f}</li>)}
                    </ul>
                  </div>}
              </> : <>
                <h3 style={{
            marginTop: 0
          }}>
                  Request {actionResult.status === "APPROVED" ? "approved" : "rejected"}
                </h3>
                <p style={{
            fontSize: "0.95em"
          }}>
                  {displayName(actionResult.name, masked)} &mdash; {actionResult.days}
                </p>
                {actionResult.emailed ? <p style={{
            fontSize: "0.92em",
            color: "#15803d"
          }}>
                    An email has been sent telling them the outcome
                    {actionResult.remaining != null && ` and that ${actionResult.remaining.toFixed(1)} hours of allowance remain`}
                    . It is recorded in their messages.
                  </p> : <p style={{
            fontSize: "0.92em",
            color: "#CF1322"
          }}>
                    The decision is saved, but no email was sent
                    {actionResult.emailError ? ` — ${actionResult.emailError}` : ""}.
                  </p>}
              </>}
            <div style={{
          textAlign: "right",
          marginTop: 12
        }}>
              <button onClick={() => setActionResult(null)} style={{
            background: "#0958D9",
            color: "#fff",
            border: 0,
            borderRadius: 4,
            padding: "6px 14px"
          }}>Close</button>
            </div>
          </div>
        </div>}

      {addOpen && <AddDialog kind={addOpen} branch={activeBranch} staff={data?.staff || []} post={post} masked={masked} onClose={changed => {
      setAddOpen(null);
      if (changed) setCursor(new Date(cursor));
    }} />}

      {threshOpen && thresholds && !thresholds.error && <ThresholdGridDialog thresholds={thresholds} post={post} masked={masked} activeDbName={activeBranch?.dbName} isHidden={isHidden} onApplied={applyThresholdChange} onHelp={() => setToleranceHelp(true)} onClose={() => {
      setThreshOpen(false);
      loadThresholds();
    }} />}

      {toleranceHelp && <ToleranceHelpDialog target={target} tolerance={tolerance} onClose={() => setToleranceHelp(false)} onConfigure={thresholds && !thresholds.error ? () => {
      setToleranceHelp(false);
      setThreshOpen(true);
    } : null} />}
    </div>;
};

/** Add leave or an absence. Builds the same days[] shape the other app writes. */
const AddDialog = ({
  kind,
  branch,
  staff,
  post,
  masked,
  onClose
}) => {
  const [staffId, setStaffId] = useState("");
  const [type, setType] = useState(kind === "leave" ? "FULL_DAY" : "SICK_LEAVE");
  const [from, setFrom] = useState(iso(new Date()));
  const [to, setTo] = useState(iso(new Date()));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const types = kind === "leave" ? ["FULL_DAY", "HALF_DAY_MORNING", "HALF_DAY_AFTERNOON", "CUSTOM_HOURS"] : ["SICK_LEAVE", "UNAUTHORISED", "EMERGENCY", "OTHER"];
  const submit = async () => {
    if (!staffId) {
      setErr("Choose a staff member");
      return;
    }
    if (new Date(to) < new Date(from)) {
      setErr("End date is before the start date");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      // one entry per day in the range, matching the shape already in use
      const days = [];
      const c = new Date(from);
      const end = new Date(to);
      while (c <= end) {
        days.push(kind === "leave" ? {
          date: iso(c),
          leaveType: type
        } : {
          date: iso(c),
          absenceType: type
        });
        c.setDate(c.getDate() + 1);
      }
      await post(kind === "leave" ? "/api/holidayRota/addLeave" : "/api/holidayRota/addAbsence", {
        dbName: branch.dbName,
        pharmacyId: branch.pharmacyId,
        staffId,
        days,
        startDate: from,
        endDate: to,
        ...(kind === "leave" ? {
          reason: note || null,
          status: "APPROVED"
        } : {
          notes: note
        })
      });
      onClose(true);
    } catch (e) {
      setBusy(false);
      setErr(`Could not save: ${e.message}`);
    }
  };
  return <div style={{
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 100
  }}>
      <div style={{
      background: "#fff",
      padding: 18,
      width: 400,
      borderRadius: 6
    }}>
        <h3 style={{
        marginTop: 0
      }}>
          {kind === "leave" ? "Add holiday" : "Record absence"}
        </h3>

        <label style={{
        fontSize: "0.9em"
      }}>Staff member</label>
        <select value={staffId} onChange={e => setStaffId(e.target.value)} style={{
        width: "100%",
        padding: 6,
        marginBottom: 10
      }}>
          <option value="">Select…</option>
          {staff.map(s => <option key={s.id} value={s.id}>
              {displayName(s.name, masked)}{s.role ? ` — ${s.role}` : ""}
            </option>)}
        </select>

        <label style={{
        fontSize: "0.9em"
      }}>Type</label>
        <select value={type} onChange={e => setType(e.target.value)} style={{
        width: "100%",
        padding: 6,
        marginBottom: 10
      }}>
          {types.map(t => <option key={t} value={t}>{pretty(t)}</option>)}
        </select>

        <div style={{
        display: "flex",
        gap: 8,
        marginBottom: 10
      }}>
          <div style={{
          flex: 1
        }}>
            <label style={{
            fontSize: "0.9em"
          }}>From</label>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={{
            width: "100%",
            padding: 6
          }} />
          </div>
          <div style={{
          flex: 1
        }}>
            <label style={{
            fontSize: "0.9em"
          }}>To</label>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} style={{
            width: "100%",
            padding: 6
          }} />
          </div>
        </div>

        <label style={{
        fontSize: "0.9em"
      }}>
          {kind === "leave" ? "Reason (optional)" : "Notes (optional)"}
        </label>
        <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} style={{
        width: "100%",
        padding: 6,
        marginBottom: 10
      }} />

        {err && <div style={{
        color: "#CF1322",
        fontSize: "0.9em",
        marginBottom: 8
      }}>{err}</div>}

        <div style={{
        textAlign: "right"
      }}>
          <button onClick={() => onClose(false)} disabled={busy} style={{
          padding: "6px 12px",
          marginRight: 8,
          background: "#fff",
          color: "#262626",
          border: "1px solid #D9D9D9",
          borderRadius: 4
        }}>Cancel</button>
          <button onClick={submit} disabled={busy} style={{
          padding: "6px 12px",
          background: "#0958D9",
          color: "#fff",
          border: 0,
          borderRadius: 4
        }}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>;
};
export default HolidayRota;

/**
 * The key to a day cell: what S, H, A, ✓ and % mean and how the day's colour
 * is decided. Wording follows the chosen cover basis and target.
 */
function RotaKey({
  onClose,
  onToleranceHelp,
  basis,
  target,
  method = "percent"
}) {
  const pctText = basis === "days" ? "people available (✓) as a share of people scheduled (S)." : basis === "hours" ? "hours available as a share of hours scheduled. Part-day leave or a short shift lowers it, so it can differ from ✓ ÷ S." : "hours available as a share of hours scheduled, where hours are known (otherwise people: ✓ ÷ S). Part-day leave or a short shift lowers it, so it can differ from ✓ ÷ S.";
  const cell = {
    padding: "3px 8px",
    verticalAlign: "top"
  };
  const letter = {
    ...cell,
    fontWeight: "bold",
    textAlign: "center",
    width: 26,
    whiteSpace: "nowrap"
  };
  const h = {
    margin: "14px 0 4px",
    fontSize: "1em",
    color: "#262626"
  };
  const red = <span style={{
    color: STATUS.short.text,
    fontWeight: "bold"
  }}>red</span>;
  const green = <span style={{
    color: STATUS.ok.text,
    fontWeight: "bold"
  }}>green</span>;
  const amber = <span style={{
    color: STATUS.over.text,
    fontWeight: "bold"
  }}>amber</span>;
  return <div style={{
    padding: "16px 20px",
    fontSize: "0.9em",
    color: "#434343",
    lineHeight: 1.45
  }}>
      <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center"
    }}>
        <h3 style={{
        margin: 0,
        color: "#262626"
      }}>How to use this page</h3>
        <button onClick={onClose} style={{
        background: "#fff",
        color: "#262626",
        border: "1px solid #D9D9D9",
        borderRadius: 4,
        padding: "4px 10px",
        cursor: "pointer"
      }}>Close</button>
      </div>

      <h4 style={h}>Finding your way</h4>
      <ul style={{
      margin: "0 0 6px 18px",
      padding: 0
    }}>
        <li>Choose the <b>branch</b> from the drop-down in the heading just above the grid. Beside it, step
            back or forward with <b>‹ ›</b>, jump with the <b>month</b> and <b>year</b> drop-downs or <b>Today</b>,
            and switch between <b>Week</b>, <b>Month</b> and <b>Year</b>.</li>
        <li>The week view names everyone due in that day — working, on holiday or absent. People not
            scheduled are left out there, but are listed in a day&rsquo;s detail.</li>
        <li><b>Filter on</b> chooses which roles are on the rota at all. Untick a role and it disappears
            completely — not shown, not counted, never colours a day. Group Manager starts unticked. Your
            choice is remembered on this browser.</li>
        <li><b>Click any day</b> to see everyone in or off that day and the change history for it. Holiday and absence
            are viewed here, not cancelled — cancellations are managed elsewhere.</li>
        <li>The panel on the right lists <b>every</b> holiday request for the branch — outstanding ones soonest
            first, decided ones newest first — whatever period the grid is showing. Click an outstanding one to
            approve or reject it.</li>
      </ul>

      <h4 style={h}>How to read a day</h4>
      <div style={{
      marginBottom: 6
    }}>
        Each day lists the roles due in, one row per role. Read a row left to right: of the people
        scheduled, how many are on holiday, how many are absent, and how many are left to cover the day.
      </div>
      <table style={{
      borderCollapse: "collapse",
      marginBottom: 6,
      fontSize: "inherit",
      color: "inherit"
    }}>
        <tbody>
          <tr><td style={letter}>S</td><td style={cell}><b>Scheduled</b> — people in that role due to work that day, from their working pattern.</td></tr>
          <tr><td style={{
            ...letter,
            color: "#0958D9"
          }}>H</td><td style={cell}><b>Holiday</b> — of those scheduled, how many are on approved or pending leave.</td></tr>
          <tr><td style={{
            ...letter,
            color: "#CF1322"
          }}>A</td><td style={cell}><b>Absent</b> — of those scheduled, how many are off sick or have another recorded absence.</td></tr>
          <tr><td style={letter}>✓</td><td style={cell}><b>Available</b> — how many are actually working: clocked in, or assumed working where nothing was recorded.</td></tr>
          {method === "percent" && <tr><td style={letter}>%</td><td style={cell}><b>Cover</b> — {pctText} {green} at or above {target}%, {red} below.</td></tr>}
          {method === "staff" && <tr><td style={letter}>M/O</td><td style={cell}><b>Min / Opt staff</b> — from the Staff thresholds grid.
              The row is {red} when ✓ is below Min, {green} from Min up to and including Opt, and {amber} above Opt.</td></tr>}
          {method === "hours" && <tr><td style={letter}>Hrs</td><td style={cell}><b>Hours available</b> for the role that day, judged against its
              Min / Opt hours: {red} below Min, {green} from Min up to and including Opt, {amber} above Opt.
              Hover a row for the figures.</td></tr>}
        </tbody>
      </table>
      <div style={{
      marginBottom: 6
    }}>
        <b>Example:</b> Driver 7 · 1 · 0 · 6 · 86% — seven drivers were due, one is on holiday, none absent,
        so six are working: 86% cover.
      </div>

      <h4 style={h}>Spotting a problem day</h4>
      <div style={{
      marginBottom: 6
    }}>
        A day that is short gets a <span style={{
        background: "#CF1322",
        color: "#fff",
        padding: "0 5px",
        borderRadius: 2,
        fontWeight: "bold"
      }}>red bar</span> across the top naming the roles responsible
        {method !== "percent" && <>, and a day with more on than needed an <span style={{
          background: "#FA8C16",
          color: "#422006",
          padding: "0 5px",
          borderRadius: 2,
          fontWeight: "bold"
        }}>amber bar</span></>}.
        Only the rows causing it are shaded. The stripe down the left of each day takes the same colour, and a
        day that is fine shows a small green {method === "percent" ? "figure" : "OK"} instead.
        {method === "percent" ? ` The day is judged on its weakest role — the lowest cover — against the acceptable cover (${target}%).` : " The day takes its worst role — Short beats Over beats OK. A role with nobody rostered on an open day counts as 0 and is judged too."}
      </div>

      <h4 style={h}>What decides the colours</h4>
      <div>
        Either the single <b>% availability</b> figure, or a <b>Min / Opt</b> per role per branch set under
        <b> ⚙ Staff thresholds</b>. The line above the grid always says which is in use for this branch
        ({method === "percent" ? "currently % availability" : `currently ${method} thresholds`}).
        {onToleranceHelp && <> {" "}
            <button onClick={onToleranceHelp} style={{
          background: "none",
          border: "none",
          color: "#0958D9",
          cursor: "pointer",
          padding: 0,
          textDecoration: "underline",
          fontSize: "inherit"
        }}>
              Full explanation of the two options
            </button>
          </>}
      </div>
    </div>;
}

/* ======================================================================
 * COVER TOLERANCE (v3) — status line, explanation, and the threshold grid
 * ==================================================================== */

/** The one-line "which rule is in use" message above the rota, with its ⓘ. */
function ToleranceStatus({
  tolerance,
  target,
  branchName,
  onHelp,
  onConfigure,
  canConfigure
}) {
  const t = tolerance;
  const setWord = t.mode === "hours" ? "Hours" : "Staff";
  let tone = "warn";
  let text;
  let detail = null;
  if (t.reason === "loading") {
    tone = "muted";
    text = "Checking staff / hours thresholds…";
  } else if (t.reason === "unavailable") {
    text = "Staff / hours thresholds could not be loaded — using % availability";
    detail = `acceptable cover ${target}%`;
  } else if (t.method === "staff") {
    tone = "good";
    text = "Staff numbers used as tolerance";
    detail = `${branchName || "this branch"} · red below Min, green from Min up to Opt, amber above Opt`;
  } else if (t.method === "hours") {
    tone = "good";
    text = "Hours used as tolerance";
    detail = `${branchName || "this branch"} · red below Min, green from Min up to Opt, amber above Opt`;
  } else if (t.required.length === 0) {
    text = `${setWord} tolerance grid not complete — using % availability`;
    detail = `no staffed roles on the rota at ${branchName || "this branch"}, so there is nothing to set`;
  } else {
    text = `${setWord} tolerance grid not complete — using % availability`;
    const set = t.required.length - t.missing.length - t.invalid.length;
    detail = `${branchName || "this branch"}: ${set} of ${t.required.length} roles set` + (t.invalid.length ? `, ${t.invalid.length} with Min above Opt (${t.invalid.join(", ")})` : "") + ` · acceptable cover ${target}%`;
  }
  const palette = {
    good: {
      bg: "#F6FFED",
      border: "#D9F7BE",
      fg: "#389E0D"
    },
    warn: {
      bg: "#FFFBE6",
      border: "#FFE58F",
      fg: "#AD4E00"
    },
    muted: {
      bg: "#FAFAFA",
      border: "#F0F0F0",
      fg: "#5D5D5D"
    }
  }[tone];
  return <div style={{
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    background: palette.bg,
    border: `1px solid ${palette.border}`,
    color: palette.fg,
    borderRadius: 6,
    padding: "6px 10px",
    marginBottom: 10,
    fontSize: "0.88em"
  }}>
      <button onClick={onHelp} title="What is this, and what do I need to do?" aria-label="Explain cover tolerance" style={{
      width: 22,
      height: 22,
      borderRadius: "50%",
      border: `1px solid ${palette.fg}`,
      background: "#fff",
      color: palette.fg,
      cursor: "pointer",
      padding: 0,
      fontWeight: "bold",
      fontSize: "0.85em",
      lineHeight: "20px",
      flex: "0 0 auto"
    }}>
        i
      </button>
      <strong>{text}</strong>
      {detail && <span style={{
      opacity: 0.85
    }}>{detail}</span>}
      {t.branchCount > 1 && t.reason !== "loading" && t.reason !== "unavailable" && <span style={{
      opacity: 0.7
    }}>
          · {t.branchesComplete} of {t.branchCount} branches complete ({setWord.toLowerCase()})
        </span>}
      {canConfigure && t.method === "percent" && t.required.length > 0 && <button onClick={onConfigure} style={{
      marginLeft: "auto",
      background: "#fff",
      color: "#0958D9",
      border: "1px solid #BAE7FF",
      borderRadius: 4,
      padding: "3px 9px",
      cursor: "pointer",
      fontSize: "0.95em"
    }}>
          {t.reason === "empty" ? "Set thresholds" : "Complete the grid"}
        </button>}
    </div>;
}

/** The full explanation behind the ⓘ. */
function ToleranceHelpDialog({
  target,
  tolerance,
  onClose,
  onConfigure
}) {
  const h = {
    margin: "14px 0 4px",
    fontSize: "1em",
    color: "#262626"
  };
  const p = {
    margin: "0 0 6px"
  };
  const sw = s => <span style={{
    display: "inline-block",
    width: 10,
    height: 10,
    borderRadius: 2,
    background: STATUS[s].stripe,
    marginRight: 4,
    verticalAlign: "middle"
  }} />;
  const t = tolerance;
  return <div onClick={onClose} style={{
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.4)",
    zIndex: 160,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  }}>
      <div onClick={e => e.stopPropagation()} style={{
      background: "#fff",
      borderRadius: 8,
      width: 640,
      maxWidth: "94vw",
      maxHeight: "86vh",
      overflowY: "auto",
      padding: "16px 20px",
      fontSize: "0.9em",
      color: "#434343",
      lineHeight: 1.45
    }}>
        <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
      }}>
          <h3 style={{
          margin: 0,
          color: "#262626"
        }}>Cover tolerance — how a day is judged</h3>
          <button onClick={onClose} style={{
          background: "#fff",
          color: "#262626",
          border: "1px solid #D9D9D9",
          borderRadius: 4,
          padding: "4px 10px",
          cursor: "pointer"
        }}>Close</button>
        </div>

        <h4 style={h}>What this is for</h4>
        <p style={p}>
          The rota colours each day, and each role within it, so you can see at a glance where holiday
          and absence leave a branch too thin to run safely — before you approve more leave, not after.
          There are two ways to decide what "too thin" means.
        </p>

        <h4 style={h}>Option A — % availability (the default)</h4>
        <p style={p}>
          Each role's available hours (or people) as a share of those scheduled, compared with one
          "Acceptable cover" figure — currently <b>{target}%</b>. {sw("short")}Red below it,{" "}
          {sw("ok")}green at or above. Quick to set up, but a percentage says nothing about absolute
          numbers: one of two pharmacists off is 50%, one of ten drivers off is 90%, and neither tells
          you whether the branch can open.
        </p>

        <h4 style={h}>Option B — Min / Opt thresholds</h4>
        <p style={p}>
          For every branch and every role you set two numbers: <b>Min</b>, the fewest you can safely run
          on, and <b>Opt</b>, the most you actually need. For each role on each day:
        </p>
        <ul style={{
        margin: "0 0 6px 18px",
        padding: 0
      }}>
          <li>{sw("short")}<b>Red</b> — available is <b>below Min</b>: short.</li>
          <li>{sw("ok")}<b>Green</b> — <b>from Min up to and including Opt</b>: right level. Exactly Min,
              or exactly Opt, is green.</li>
          <li>{sw("over")}<b>Amber</b> — <b>above Opt</b>: more on than needed, room to approve leave.</li>
        </ul>
        <p style={p}>
          <b>Example:</b> Dispenser Min 2, Opt 3. With 1 dispenser available the cell is red; with 2 or 3 it
          is green; with 4 it is amber. To say "we need at least one pharmacist, and one is enough", set
          Pharmacist Min 1, Opt 1.
        </p>
        <p style={p}>
          The numbers can be held as <b>Staff</b> (people available, the ✓ column) or <b>Hours</b> (hours
          available that day, allowing for half days and short shifts). Staff and Hours are kept as two
          separate sets — switching the toggle never loses either — and the one selected is the one the
          rota uses, for every branch.
        </p>

        <h4 style={h}>Which one is used</h4>
        <p style={p}>
          A branch is judged on thresholds only when its row in the selected set is <b>complete</b>: every
          role defined at that branch has both Min and Opt, and Min is not above Opt. If any cell is empty
          the whole branch stays on % availability. That is deliberate — a grid with holes in it would have
          some roles judged and others silently not, and a day could look fine only because nobody had
          filled in the role that was short. The status line above the rota always says which rule is in
          use; the day's colour is its worst role (short beats over beats OK).
        </p>

        <h4 style={h}>What you need to do</h4>
        <ol style={{
        margin: "0 0 6px 18px",
        padding: 0
      }}>
          <li>Click <b>⚙ Staff thresholds</b>.</li>
          <li>Choose <b>Staff</b> or <b>Hours</b> at the top of the grid.</li>
          <li>Fill Min and Opt for every role on every branch row. Cells marked <i>n/a</i> are roles nobody at
              that branch holds — nothing is needed there. Empty cells are shaded so the gaps are easy to spot.</li>
          <li>Values save as you type; there is no Save button. "Copy to all" on a row fills every other
              branch with the same numbers as a starting point.</li>
          <li>Close the grid. The status line should now read "Staff numbers used as tolerance" or
              "Hours used as tolerance". To go back to %, clear any cell in that branch's row.</li>
        </ol>
        <p style={p}>
          "Filter on" applies either way: a role you untick disappears from the rota completely — it is
          not shown, not counted, never colours a day, and needs no thresholds for the grid to count as
          complete. Group Manager starts unticked.
        </p>

        <div style={{
        marginTop: 12,
        padding: "8px 10px",
        background: "#FAFAFA",
        border: "1px solid #F0F0F0",
        borderRadius: 4
      }}>
          <b>This branch right now:</b>{" "}
          {t.method === "staff" ? "judged on staff thresholds." : t.method === "hours" ? "judged on hours thresholds." : t.reason === "loading" ? "checking…" : t.reason === "unavailable" ? "thresholds could not be loaded, so % availability is used." : t.required.length === 0 ? "no staffed roles are on the rota, so % availability is used." : `% availability — the ${t.mode} grid is missing ${t.missing.length} role${t.missing.length === 1 ? "" : "s"}` + `${t.missing.length ? ` (${t.missing.join(", ")})` : ""}` + `${t.invalid.length ? `, and ${t.invalid.join(", ")} ha${t.invalid.length === 1 ? "s" : "ve"} Min above Opt` : ""}.`}
          {onConfigure && <button onClick={onConfigure} style={{
          marginLeft: 10,
          background: "#0958D9",
          color: "#fff",
          border: 0,
          borderRadius: 4,
          padding: "4px 10px",
          cursor: "pointer"
        }}>
              Open the grid
            </button>}
        </div>
      </div>
    </div>;
}

/** "" -> null (cleared); bad -> undefined; otherwise the number. */
const parseThreshold = (s, mode) => {
  const v = String(s ?? "").trim();
  if (v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return undefined;
  if (mode === "staff" && !Number.isInteger(n)) return undefined;
  if (n > (mode === "hours" ? 5000 : 500)) return undefined;
  return mode === "hours" ? Math.round(n * 100) / 100 : n;
};

/**
 * Branches down, roles across, Min and Opt in every cell. Each change is
 * saved on its own a moment after typing stops, so there is no Save button
 * to forget; closing flushes anything still waiting.
 */
function ThresholdGridDialog({
  thresholds,
  post,
  masked,
  activeDbName,
  isHidden = () => false,
  onApplied,
  onHelp,
  onClose
}) {
  const branches = thresholds.branches || [];
  const roles = thresholds.roles || [];
  const branchRoles = thresholds.branchRoles || {};
  const [mode, setMode] = useState(thresholds.mode === "hours" ? "hours" : "staff");
  const [draft, setDraft] = useState(() => {
    const out = {
      staff: {},
      hours: {}
    };
    for (const m of ["staff", "hours"]) {
      for (const [db, rs] of Object.entries(thresholds.cells?.[m] || {})) {
        out[m][db] = {};
        for (const [role, c] of Object.entries(rs)) {
          out[m][db][role] = {
            min: c.min == null ? "" : String(c.min),
            opt: c.opt == null ? "" : String(c.opt)
          };
        }
      }
    }
    return out;
  });
  const [cellState, setCellState] = useState({}); // key -> pending|saving|saved|error|invalid
  const [saveErr, setSaveErr] = useState(null);
  const [modeErr, setModeErr] = useState(null);
  const [confirmCopy, setConfirmCopy] = useState(null);
  const [closing, setClosing] = useState(false);
  const timers = useRef({});
  const unit = mode === "hours" ? "hours" : "staff";
  const keyOf = (m, db, role, f) => `${m}|${db}|${role}|${f}`;
  const markCell = (k, s) => setCellState(x => ({
    ...x,
    [k]: s
  }));
  const valueOf = (db, role, f) => draft[mode]?.[db]?.[role]?.[f] ?? "";
  const edit = (db, role, field, str) => {
    const m = mode;
    setDraft(d => {
      const next = {
        ...d,
        [m]: {
          ...d[m]
        }
      };
      next[m][db] = {
        ...(next[m][db] || {})
      };
      next[m][db][role] = {
        min: "",
        opt: "",
        ...(next[m][db][role] || {}),
        [field]: str
      };
      return next;
    });
    const k = keyOf(m, db, role, field);
    if (timers.current[k]) clearTimeout(timers.current[k].t);
    delete timers.current[k];
    const v = parseThreshold(str, m);
    if (v === undefined) {
      markCell(k, "invalid");
      return;
    }
    markCell(k, "pending");
    const run = async () => {
      delete timers.current[k];
      markCell(k, "saving");
      try {
        await post("/api/holidayRota/thresholds/set", {
          mode: m,
          dbName: db,
          role,
          [field]: v
        });
        markCell(k, "saved");
        onApplied({
          set: m,
          cells: [{
            dbName: db,
            role,
            [field]: v
          }]
        });
      } catch (e) {
        markCell(k, "error");
        setSaveErr(`Could not save ${role} ${field === "min" ? "Min" : "Opt"}: ${e.message}`);
      }
    };
    timers.current[k] = {
      t: setTimeout(run, 600),
      run
    };
  };
  const close = async () => {
    if (closing) return;
    setClosing(true);
    const pending = Object.values(timers.current);
    timers.current = {};
    pending.forEach(p => clearTimeout(p.t));
    await Promise.all(pending.map(p => p.run()));
    onClose();
  };
  const switchMode = async m => {
    if (m === mode) return;
    setMode(m);
    setModeErr(null);
    try {
      await post("/api/holidayRota/thresholds/mode", {
        mode: m
      });
      onApplied({
        mode: m
      });
    } catch (e) {
      setModeErr(`Could not save the Staff/Hours choice: ${e.message}`);
    }
  };

  /** Completeness of one branch row in the visible set. */
  const rowState = db => {
    // roles filtered off the rota don't need values for the row to count
    const req = (branchRoles[db] || []).filter(r => !isHidden(r));
    let missing = 0,
      invalid = 0;
    for (const role of req) {
      const mn = parseThreshold(valueOf(db, role, "min"), mode);
      const op = parseThreshold(valueOf(db, role, "opt"), mode);
      if (mn === undefined || op === undefined) invalid += 1;else if (mn === null || op === null) missing += 1;else if (mn > op) invalid += 1;
    }
    return {
      req: req.length,
      missing,
      invalid,
      complete: req.length > 0 && !missing && !invalid
    };
  };

  /** Copy one branch row into every other branch, for the roles each one has. */
  const copyRowToAll = async srcDb => {
    setConfirmCopy(null);
    const m = mode;
    const cells = [];
    for (const b of branches) {
      if (b.dbName === srcDb) continue;
      for (const role of branchRoles[b.dbName] || []) {
        const mn = parseThreshold(valueOf(srcDb, role, "min"), m);
        const op = parseThreshold(valueOf(srcDb, role, "opt"), m);
        if (mn == null || op == null) continue; // only copy what is actually set
        cells.push({
          dbName: b.dbName,
          role,
          min: mn,
          opt: op
        });
      }
    }
    if (!cells.length) {
      setSaveErr("Nothing to copy — that row has no complete cells.");
      return;
    }
    try {
      await post("/api/holidayRota/thresholds/setMany", {
        mode: m,
        cells
      });
      setDraft(d => {
        const next = {
          ...d,
          [m]: {
            ...d[m]
          }
        };
        for (const c of cells) {
          next[m][c.dbName] = {
            ...(next[m][c.dbName] || {})
          };
          next[m][c.dbName][c.role] = {
            min: String(c.min),
            opt: String(c.opt)
          };
        }
        return next;
      });
      onApplied({
        set: m,
        cells
      });
      setSaveErr(null);
    } catch (e) {
      setSaveErr(`Could not copy the row: ${e.message}`);
    }
  };
  const states = Object.entries(cellState).filter(([k]) => k.startsWith(`${mode}|`)).map(([, s]) => s);
  const busy = states.some(s => s === "pending" || s === "saving");
  const failed = states.some(s => s === "error");
  const completeRows = branches.filter(b => rowState(b.dbName).complete).length;
  const inputStyle = (k, empty, clash) => {
    const s = cellState[k];
    return {
      width: 46,
      padding: "3px 4px",
      fontSize: "0.92em",
      textAlign: "right",
      borderRadius: 3,
      boxSizing: "border-box",
      border: `1px solid ${s === "invalid" || s === "error" || clash ? "#CF1322" : s === "saved" ? "#389E0D" : "#D9D9D9"}`,
      background: empty ? "#FFFBE6" : "#fff",
      color: "#262626",
      MozAppearance: "textfield"
    };
  };
  const th = {
    padding: "6px 8px",
    background: "#F5F5F5",
    borderBottom: "1px solid #D9D9D9",
    position: "sticky",
    top: 0,
    zIndex: 2,
    fontSize: "0.85em",
    whiteSpace: "nowrap"
  };
  return <div style={{
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.4)",
    zIndex: 150,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  }}>
      <div style={{
      background: "#fff",
      borderRadius: 8,
      width: "min(96vw, 1400px)",
      maxHeight: "92vh",
      display: "flex",
      flexDirection: "column",
      padding: "14px 18px",
      color: "#434343"
    }}>
        <div style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        flexWrap: "wrap"
      }}>
          <h3 style={{
          margin: 0,
          color: "#262626"
        }}>Staff thresholds — Min / Opt cover</h3>
          <button onClick={onHelp} title="Full explanation" style={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          border: "1px solid #0958D9",
          background: "#fff",
          color: "#0958D9",
          cursor: "pointer",
          padding: 0,
          fontWeight: "bold",
          fontSize: "0.85em"
        }}>i</button>

          {/* Staff / Hours: two separate sets; the selected one is what the rota uses */}
          <div style={{
          display: "inline-flex",
          border: "1px solid #D9D9D9",
          borderRadius: 6,
          overflow: "hidden",
          marginLeft: 12
        }} title="Staff and Hours are stored separately — switching never loses either. The one selected is the one the rota uses.">
            {[["staff", "Staff"], ["hours", "Hours"]].map(([m, label]) => <button key={m} onClick={() => switchMode(m)} style={{
            padding: "5px 14px",
            border: 0,
            cursor: "pointer",
            background: mode === m ? "#0958D9" : "#fff",
            color: mode === m ? "#fff" : "#262626",
            fontWeight: mode === m ? "bold" : "normal"
          }}>{label}</button>)}
          </div>

          <span style={{
          fontSize: "0.85em",
          color: busy ? "#AD4E00" : failed ? "#CF1322" : "#15803d"
        }}>
            {busy ? "Saving…" : failed ? "Some changes did not save" : "All changes saved"}
          </span>
          <span style={{
          fontSize: "0.85em",
          color: "#5D5D5D"
        }}>
            · {completeRows} of {branches.length} branches complete
          </span>

          <button onClick={close} disabled={closing} style={{
          marginLeft: "auto",
          background: "#0958D9",
          color: "#fff",
          border: 0,
          borderRadius: 4,
          padding: "6px 14px",
          cursor: "pointer"
        }}>
            {closing ? "Saving…" : "Done"}
          </button>
        </div>

        <div style={{
        fontSize: "0.86em",
        margin: "10px 0",
        lineHeight: 1.45,
        maxWidth: 1000
      }}>
          For each branch and role, set the <b>Min</b>imum and <b>Opt</b>imum number of{" "}
          <b>{mode === "hours" ? "hours available in the day" : "staff available"}</b>. On the rota, for each
          role on each day: <span style={{
          color: STATUS.short.text,
          fontWeight: "bold"
        }}>red</span> when it
          falls <b>below Min</b>, <span style={{
          color: STATUS.ok.text,
          fontWeight: "bold"
        }}>green</span>{" "}
          when it is <b>from Min up to and including Opt</b>, and{" "}
          <span style={{
          color: STATUS.over.text,
          fontWeight: "bold"
        }}>amber</span> when it is <b>above Opt</b>.
          {mode === "hours" ? " For example, Dispenser Min 7.5, Opt 15: 6 dispenser hours available is red, anything from 7.5 to 15 is green, 22.5 is amber." : " For example, Dispenser Min 2, Opt 3: if the number of dispensers available is 1 the cell is red, 2 or 3 is green, 4 is amber."}
          {" "}Every role in a branch's row needs both values before that branch uses them — until then it stays
          on % availability. <i>n/a</i> means nobody at that branch holds that role; columns for roles filtered off the
          rota (under "Filter on") are optional. Changes save as you type.
        </div>

        {(saveErr || modeErr) && <div style={{
        background: "#FFF1F0",
        border: "1px solid #FFCCC7",
        color: "#A8071A",
        borderRadius: 4,
        padding: "6px 10px",
        fontSize: "0.85em",
        marginBottom: 8
      }}>
            {modeErr || saveErr}
            <button onClick={() => {
          setSaveErr(null);
          setModeErr(null);
        }} style={{
          marginLeft: 10,
          background: "none",
          border: 0,
          color: "#A8071A",
          cursor: "pointer",
          textDecoration: "underline"
        }}>dismiss</button>
          </div>}

        {roles.length === 0 ? <div style={{
        padding: 20,
        color: "#5D5D5D"
      }}>
            No roles are defined at any of your branches, so there is nothing to set yet.
          </div> : <div style={{
        overflow: "auto",
        border: "1px solid #F0F0F0",
        borderRadius: 4,
        flex: 1
      }}>
            <table style={{
          borderCollapse: "separate",
          borderSpacing: 0,
          fontSize: "0.88em"
        }}>
              <thead>
                <tr>
                  <th style={{
                ...th,
                left: 0,
                zIndex: 3,
                textAlign: "left",
                minWidth: 170
              }}>Branch</th>
                  {roles.map(r => <th key={r} style={{
                ...th,
                textAlign: "center"
              }}>
                      <span style={{
                  color: isHidden(r) ? "#8C8C8C" : "inherit"
                }}>{r}</span>
                      <div style={{
                  fontWeight: "normal",
                  color: "#5D5D5D",
                  fontSize: "0.9em"
                }}>
                        {isHidden(r) ? "filtered off rota — optional" : `Min · Opt ${mode === "hours" ? "(hrs)" : "(staff)"}`}
                      </div>
                    </th>)}
                  <th style={{
                ...th,
                textAlign: "left"
              }}>Row</th>
                </tr>
              </thead>
              <tbody>
                {branches.map(b => {
              const rs = rowState(b.dbName);
              const has = new Set(branchRoles[b.dbName] || []);
              const isActive = b.dbName === activeDbName;
              return <tr key={b.dbName}>
                      <td style={{
                  position: "sticky",
                  left: 0,
                  zIndex: 1,
                  padding: "6px 8px",
                  background: isActive ? "#E6F7FF" : "#fff",
                  borderBottom: "1px solid #F5F5F5",
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                  color: "#262626"
                }}>
                        {displayName(b.name, masked)}
                        {isActive && <span style={{
                    fontWeight: "normal",
                    color: "#0958D9",
                    fontSize: "0.85em"
                  }}> · showing</span>}
                      </td>
                      {roles.map(role => {
                  if (!has.has(role)) {
                    return <td key={role} style={{
                      textAlign: "center",
                      color: "#D9D9D9",
                      background: "#FAFAFA",
                      padding: "6px 8px",
                      borderBottom: "1px solid #F5F5F5"
                    }} title={`Nobody at ${b.name} holds the ${role} role`}>n/a</td>;
                  }
                  const vMin = valueOf(b.dbName, role, "min");
                  const vOpt = valueOf(b.dbName, role, "opt");
                  const pMin = parseThreshold(vMin, mode);
                  const pOpt = parseThreshold(vOpt, mode);
                  const clash = isNum(pMin) && isNum(pOpt) && pMin > pOpt;
                  const kMin = keyOf(mode, b.dbName, role, "min");
                  const kOpt = keyOf(mode, b.dbName, role, "opt");
                  return <td key={role} style={{
                    padding: "5px 8px",
                    borderBottom: "1px solid #F5F5F5",
                    textAlign: "center"
                  }} title={clash ? "Min is above Opt — Min must not be more than Opt" : undefined}>
                            <div style={{
                      display: "inline-flex",
                      gap: 6,
                      alignItems: "center"
                    }}>
                              <input type="text" inputMode={mode === "hours" ? "decimal" : "numeric"} autoComplete="off" value={vMin} placeholder="Min" aria-label={`${b.name} ${role} Min`} onChange={e => edit(b.dbName, role, "min", e.target.value)} style={inputStyle(kMin, vMin === "" && !isHidden(role), clash)} />
                              <input type="text" inputMode={mode === "hours" ? "decimal" : "numeric"} autoComplete="off" value={vOpt} placeholder="Opt" aria-label={`${b.name} ${role} Opt`} onChange={e => edit(b.dbName, role, "opt", e.target.value)} style={inputStyle(kOpt, vOpt === "" && !isHidden(role), clash)} />
                            </div>
                          </td>;
                })}
                      <td style={{
                  padding: "6px 8px",
                  borderBottom: "1px solid #F5F5F5",
                  whiteSpace: "nowrap",
                  fontSize: "0.9em"
                }}>
                        {rs.req === 0 ? <span style={{
                    color: "#8C8C8C"
                  }}>no roles</span> : rs.complete ? <span style={{
                    color: "#15803d",
                    fontWeight: "bold"
                  }}>✓ Complete</span> : <span style={{
                    color: "#AD4E00"
                  }}>
                              {rs.missing > 0 && `${rs.missing} to fill`}
                              {rs.missing > 0 && rs.invalid > 0 && " · "}
                              {rs.invalid > 0 && <span style={{
                      color: "#CF1322"
                    }}>{rs.invalid} invalid</span>}
                            </span>}
                        {branches.length > 1 && rs.req > 0 && (confirmCopy === b.dbName ? <span style={{
                    marginLeft: 8
                  }}>
                              <button onClick={() => copyRowToAll(b.dbName)} style={{
                      background: "#b45309",
                      color: "#fff",
                      border: 0,
                      borderRadius: 3,
                      padding: "2px 7px",
                      cursor: "pointer",
                      fontSize: "0.95em"
                    }}>
                                Overwrite all other branches
                              </button>
                              <button onClick={() => setConfirmCopy(null)} style={{
                      marginLeft: 4,
                      background: "#fff",
                      color: "#262626",
                      border: "1px solid #D9D9D9",
                      borderRadius: 3,
                      padding: "2px 7px",
                      cursor: "pointer",
                      fontSize: "0.95em"
                    }}>
                                Cancel
                              </button>
                            </span> : <button onClick={() => setConfirmCopy(b.dbName)} title={`Copy ${b.name}'s ${unit} values to every other branch, for the roles each one has`} style={{
                    marginLeft: 8,
                    background: "#fff",
                    color: "#0958D9",
                    border: "1px solid #BAE7FF",
                    borderRadius: 3,
                    padding: "2px 7px",
                    cursor: "pointer",
                    fontSize: "0.95em"
                  }}>
                              Copy to all
                            </button>)}
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>}
      </div>
    </div>;
}

/* ======================================================================
 * HOLIDAY REQUEST REVIEW
 *
 * Three panels:
 *   1. the request itself — every day, its type, the time off and the hours
 *      it uses, the note, and what it does to the allowance;
 *   2. each requested day as it would stand for the requester's role, with
 *      every person in that role and what they are actually working, and a
 *      verdict worded for whichever constraint is in use (hours / staff
 *      thresholds, or % cover);
 *   3. the overall verdict and the decision.
 *
 * The days are composed fresh for the request's own dates (not taken from
 * the window on screen), so a request in a month you are not looking at is
 * judged just as well. Pending requests are already composed as leave, so
 * every figure INCLUDES this request; other pending requests on the same day
 * are flagged, because approving this one may only be safe if they are not.
 * ==================================================================== */

const hm = t => t ? new Date(t).toLocaleTimeString("en-GB", {
  hour: "2-digit",
  minute: "2-digit"
}) : "";
/** 'YYYY-MM-DD' -> local midnight, the same kind of date composeDays walks. */
const localDate = key => {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const h1 = v => `${(+v || 0).toFixed(1).replace(/\.0$/, "")}h`;
function RequestReview({
  request: l,
  staffById,
  post,
  branch,
  tolerance,
  target,
  basis,
  masked,
  roleColour,
  onDecide,
  onClose
}) {
  const s = staffById.get(l.staffId);
  const role = (s?.role || "").trim() || "No role";
  const keys = [...new Set((l.days || []).map(d => isoUTC(d.date)))].sort();
  const todayKey = iso(new Date());
  const lapsed = keys.length > 0 && keys[keys.length - 1] < todayKey;
  const [src, setSrc] = useState(null); // rota data covering the request's dates
  const [srcErr, setSrcErr] = useState(null);
  const [allow, setAllow] = useState(null); // this person's allowance row
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!keys.length || !branch) return;
    let cancelled = false;
    (async () => {
      try {
        const d = await post("/api/holidayRota/data", {
          scheduleApi: 2,
          // v44: send the real Week A / Week B rows
          dbName: branch.dbName,
          pharmacyId: branch.pharmacyId,
          from: keys[0],
          to: keys[keys.length - 1]
        });
        if (!cancelled) setSrc(d);
      } catch (e) {
        if (!cancelled) setSrcErr(e.message);
      }
      try {
        const a = await post("/api/holidayRota/allowance", {
          dbName: branch.dbName,
          pharmacyId: branch.pharmacyId,
          year: Number(keys[0].slice(0, 4))
        });
        if (!cancelled) setAllow((a.staff || []).find(x => x.staffId === l.staffId) || null);
      } catch (_) {/* allowance is a nice-to-have here */}
    })();
    return () => {
      cancelled = true;
    };
  }, [l.id, branch?.dbName]); // eslint-disable-line react-hooks/exhaustive-deps

  const composed = useMemo(() => src ? composeDays(src, localDate(keys[0]), localDate(keys[keys.length - 1])) : {}, [src] // eslint-disable-line react-hooks/exhaustive-deps
  );

  /* ---- panel 1: the request's own days and hours ---- */
  const requestDays = (l.days || []).map(d => {
    const key = isoUTC(d.date);
    const pat = src ? patternFor(src.times, l.staffId, localDate(key)) : null;
    const planned = pat ? pat.hours : 7.5;
    const notRostered = pat && !pat.rostered;
    const t = d.leaveType || "FULL_DAY";
    const off = notRostered ? 0 : t.startsWith("HALF_DAY") ? planned / 2 : t === "CUSTOM_HOURS" ? Math.min(planned, hoursBetween(d.startTime, d.endTime) || planned) : planned;
    const when = t === "CUSTOM_HOURS" && d.startTime && d.endTime ? `${hm(d.startTime)}–${hm(d.endTime)}` : t === "HALF_DAY_MORNING" ? "Morning" : t === "HALF_DAY_AFTERNOON" ? "Afternoon" : "All day";
    return {
      key,
      type: t,
      when,
      planned,
      off,
      notRostered,
      noPattern: !pat
    };
  }).sort((a, b) => a.key.localeCompare(b.key));
  const totalOff = requestDays.reduce((t, d) => t + d.off, 0);

  /* ---- panel 2: each day for this role ---- */
  const thresholdKey = Object.keys(tolerance.row || {}).find(k => normRole(k) === normRole(role));
  const c = thresholdKey ? tolerance.row[thresholdKey] : null;
  const method = tolerance.method !== "percent" && c && isNum(c.min) && isNum(c.opt) ? tolerance.method : "percent";
  const judgeDay = key => {
    const all = (composed[key] || []).filter(e => normRole(e.staff.role || "No role") === normRole(role));
    const on = all.filter(e => e.state !== "off");
    const scheduled = on.length;
    const plannedH = on.reduce((t, e) => t + (e.planned || 0), 0);
    const mine = all.find(e => e.recordKind === "leave" && e.recordId === l.id) || null;
    const meEntry = all.find(e => e.staff.id === l.staffId) || null;
    const others = on.filter(e => e.pending && e.recordId !== l.id);
    const useHours = basis === "hours" || basis === "auto" && plannedH > 0;
    const score = (working, avail) => method === "staff" ? working : method === "hours" ? avail : useHours ? plannedH > 0 ? avail / plannedH * 100 : null : scheduled > 0 ? working / scheduled * 100 : null;
    const verdict = v => v === null ? null : method === "percent" ? v < target - 1e-6 ? "short" : "ok" : v < c.min - 1e-6 ? "short" : v <= c.opt + 1e-6 ? "ok" : "over";
    const working = on.filter(e => e.state === "working").length;
    const avail = on.reduce((t, e) => t + (e.hours || 0), 0);
    const back = e => ({
      w: 1,
      h: Math.max(0, (e.planned || 0) - (e.hours || 0))
    });
    const withReq = score(working, avail);
    let withoutReq = null;
    if (mine) {
      const b = back(mine);
      withoutReq = score(working + b.w, avail + b.h);
    }
    let withoutOthers = null;
    if (others.length) {
      const add = others.reduce((t, e) => {
        const b = back(e);
        return {
          w: t.w + b.w,
          h: t.h + b.h
        };
      }, {
        w: 0,
        h: 0
      });
      withoutOthers = score(working + add.w, avail + add.h);
    }
    return {
      key,
      all,
      on,
      scheduled,
      plannedH,
      working,
      avail,
      mine,
      meEntry,
      others,
      useHours,
      withReq,
      withoutReq,
      withoutOthers,
      vWith: verdict(withReq),
      vWithout: verdict(withoutReq),
      vWithoutOthers: verdict(withoutOthers)
    };
  };
  const judged = src ? keys.map(judgeDay) : [];
  const fmtScore = v => v === null || v === undefined ? "—" : method === "staff" ? `${v} staff` : method === "hours" ? h1(v) : `${v.toFixed(0)}%`;

  // A day the requester isn't working, or is off sick anyway, costs no cover.
  const affects = j => !!j.mine;
  const bad = judged.filter(j => affects(j) && j.vWith === "short");
  const dayLabel = key => {
    const x = localDate(key);
    return `${x.toLocaleDateString("en-GB", {
      weekday: "short"
    })} ${x.getDate()} ` + `${x.toLocaleDateString("en-GB", {
      month: "short"
    })} ${x.getFullYear()}`;
  };
  const panel = {
    border: "1px solid #F0F0F0",
    borderRadius: 6,
    padding: "10px 12px",
    marginBottom: 12
  };
  const panelTitle = {
    fontSize: "0.78em",
    fontWeight: "bold",
    color: "#5D5D5D",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 6
  };
  const badge = (bg, fg, text) => <span style={{
    fontSize: "0.72em",
    fontWeight: "bold",
    padding: "1px 6px",
    borderRadius: 3,
    background: bg,
    color: fg,
    marginLeft: 6,
    whiteSpace: "nowrap"
  }}>{text}</span>;

  /** One person's line: what they were due, and what they are actually doing. */
  const personLine = e => {
    const isMine = e.recordKind === "leave" && e.recordId === l.id;
    let doing;
    if (e.state === "off") doing = "Not scheduled";else if (e.state === "absent") doing = `Absent — ${e.label}`;else if (e.state === "holiday") {
      doing = e.leaveType === "CUSTOM_HOURS" && e.leaveStart && e.leaveEnd ? `Custom hours off ${hm(e.leaveStart)}–${hm(e.leaveEnd)}` : e.leaveType === "HALF_DAY_MORNING" ? "Half day off (morning)" : e.leaveType === "HALF_DAY_AFTERNOON" ? "Half day off (afternoon)" : "Holiday (full day)";
    } else doing = e.source === "clocked" ? "Working (clocked in)" : "Working";
    return <tr key={e.staff.id} style={{
      borderTop: "1px solid #F5F5F5",
      background: isMine ? "#E6F7FF" : e.pending ? "#FFFBE6" : "transparent",
      opacity: e.state === "off" ? 0.5 : 1
    }}>
        <td style={{
        padding: "4px 6px",
        fontWeight: isMine ? "bold" : "normal"
      }}>
          {displayName(e.staff.name, masked)}
          {isMine && badge("#0958D9", "#fff", "THIS REQUEST")}
          {!isMine && e.pending && badge("#FA8C16", "#422006", "PENDING")}
        </td>
        <td style={{
        padding: "4px 6px",
        textAlign: "right",
        color: "#5D5D5D"
      }}>
          {e.state === "off" ? "—" : h1(e.planned)}
        </td>
        <td style={{
        padding: "4px 6px",
        color: e.state === "holiday" ? "#0958D9" : e.state === "absent" ? "#CF1322" : "#434343"
      }}>
          {doing}
        </td>
        <td style={{
        padding: "4px 6px",
        textAlign: "right",
        fontWeight: "bold"
      }}>
          {e.state === "off" ? "—" : h1(e.hours)}
        </td>
      </tr>;
  };
  const explain = j => {
    const R = <b>{role}</b>;
    if (method === "hours") {
      return <>Role: {R}. Minimum hours needed on this day: <b>{h1(c.min)}</b>. Optimum: <b>{h1(c.opt)}</b>.
        {" "}Including this holiday request, the available hours are <b>{h1(j.avail)}</b>
        {" "}(of {h1(j.plannedH)} scheduled).</>;
    }
    if (method === "staff") {
      return <>Role: {R}. Minimum staff needed on this day: <b>{c.min}</b>. Optimum: <b>{c.opt}</b>.
        {" "}Including this holiday request, <b>{j.working}</b> of {j.scheduled} scheduled
        {j.scheduled === 1 ? " is" : " are"} available.</>;
    }
    return j.useHours ? <>Role: {R}. Acceptable cover is <b>{target}%</b> of scheduled hours — {h1(j.plannedH)} scheduled, so at
          least <b>{h1(j.plannedH * target / 100)}</b> needed. Including this holiday request, the available hours
          are <b>{h1(j.avail)}</b> — <b>{fmtScore(j.withReq)}</b>.</> : <>Role: {R}. Acceptable cover is <b>{target}%</b> of the people scheduled — {j.scheduled} scheduled, so at
          least <b>{Math.ceil(j.scheduled * target / 100 - 1e-9)}</b> needed. Including this holiday request,
          {" "}<b>{j.working}</b> {j.working === 1 ? "is" : "are"} available — <b>{fmtScore(j.withReq)}</b>.</>;
  };
  const conclusion = j => {
    if (!j.meEntry || j.meEntry.state === "off") {
      return {
        ok: true,
        text: `${s?.name || "They"} isn’t scheduled to work this day, so this day of the request doesn’t reduce cover.`
      };
    }
    if (!j.mine) {
      return {
        ok: true,
        text: `${s?.name || "They"} is already recorded as ${j.meEntry.state === "absent" ? "absent" : j.meEntry.state} this day, so the request makes no further difference to cover.`
      };
    }
    if (j.vWith === "short") {
      const already = j.vWithout === "short";
      return {
        ok: false,
        text: method === "percent" ? `Not acceptable — ${role} cover falls to ${fmtScore(j.withReq)}, below the ${target}% acceptable cover.${already ? ` It is already short without this request (${fmtScore(j.withoutReq)}).` : ""}` : `Not acceptable — ${role} falls to ${fmtScore(j.withReq)}, below the minimum of ${fmtScore(c.min)}.${already ? ` It is already short without this request (${fmtScore(j.withoutReq)}).` : ""}`
      };
    }
    if (j.vWith === "over") {
      return {
        ok: true,
        text: `Acceptable — even with this request ${role} has ${fmtScore(j.withReq)}, above the optimum of ${fmtScore(c.opt)}.`
      };
    }
    return {
      ok: true,
      text: method === "percent" ? `Acceptable — ${role} cover stays at ${fmtScore(j.withReq)}, at or above the ${target}% acceptable cover.` : `Acceptable — ${role} stays within Min–Opt at ${fmtScore(j.withReq)}.`
    };
  };
  const decide = async status => {
    if (busy) return;
    setBusy(true);
    try {
      await onDecide(l, status);
    } finally {
      setBusy(false);
    }
  };
  return <div onClick={onClose} style={{
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.35)",
    zIndex: 110,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  }}>
      <div onClick={ev => ev.stopPropagation()} style={{
      background: "#fff",
      borderRadius: 8,
      width: 760,
      maxWidth: "95vw",
      maxHeight: "90vh",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden"
    }}>
        <div style={{
        padding: "14px 16px 8px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        borderBottom: "1px solid #F0F0F0"
      }}>
          <div>
            <h3 style={{
            margin: 0
          }}>Holiday request — {displayName(s?.name || "Unknown", masked)}</h3>
            <div style={{
            fontSize: "0.88em",
            color: "#5D5D5D",
            marginTop: 2
          }}>
              <span style={{
              color: roleColour.get(s?.roleId) || "#434343",
              fontWeight: "bold"
            }}>{role}</span>
              {" · "}{displayName(branch?.name, masked)}
              {badge(lapsed ? "#F0F0F0" : "#FFF7E6", lapsed ? "#5D5D5D" : "#AD4E00", lapsed ? "PENDING — DATES PASSED" : "PENDING")}
            </div>
          </div>
          <button onClick={onClose} style={{
          background: "#fff",
          color: "#262626",
          border: "1px solid #D9D9D9",
          borderRadius: 4,
          padding: "4px 10px",
          cursor: "pointer"
        }}>Close</button>
        </div>

        <div style={{
        overflowY: "auto",
        padding: "12px 16px",
        flex: "1 1 auto",
        minHeight: 0
      }}>
          {/* ---------------- 1. the request ---------------- */}
          <div style={panel}>
            <div style={panelTitle}>1 · The request</div>
            <table style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "0.88em"
          }}>
              <thead>
                <tr style={{
                color: "#5D5D5D",
                textAlign: "left"
              }}>
                  <th style={{
                  padding: "3px 6px"
                }}>Date</th>
                  <th style={{
                  padding: "3px 6px"
                }}>Type</th>
                  <th style={{
                  padding: "3px 6px"
                }}>Time off</th>
                  <th style={{
                  padding: "3px 6px",
                  textAlign: "right"
                }}>Usual hours</th>
                  <th style={{
                  padding: "3px 6px",
                  textAlign: "right"
                }}>Hours used</th>
                </tr>
              </thead>
              <tbody>
                {requestDays.map(d => <tr key={d.key} style={{
                borderTop: "1px solid #F5F5F5"
              }}>
                    <td style={{
                  padding: "3px 6px",
                  whiteSpace: "nowrap"
                }}>{dayLabel(d.key)}</td>
                    <td style={{
                  padding: "3px 6px"
                }}>{pretty(d.type)}</td>
                    <td style={{
                  padding: "3px 6px"
                }}>{d.when}</td>
                    <td style={{
                  padding: "3px 6px",
                  textAlign: "right",
                  color: "#5D5D5D"
                }} title={d.noPattern ? "No working pattern on file — a 7.5h day is assumed" : undefined}>
                      {!src ? "…" : d.notRostered ? "not scheduled" : `${h1(d.planned)}${d.noPattern ? "*" : ""}`}
                    </td>
                    <td style={{
                  padding: "3px 6px",
                  textAlign: "right",
                  fontWeight: "bold"
                }}>
                      {!src ? "…" : h1(d.off)}
                    </td>
                  </tr>)}
                <tr style={{
                borderTop: "1px solid #D9D9D9"
              }}>
                  <td colSpan={4} style={{
                  padding: "4px 6px",
                  textAlign: "right",
                  color: "#5D5D5D"
                }}>
                    Total — {requestDays.length} day{requestDays.length === 1 ? "" : "s"}
                  </td>
                  <td style={{
                  padding: "4px 6px",
                  textAlign: "right",
                  fontWeight: "bold"
                }}>
                    {src ? h1(totalOff) : "…"}
                  </td>
                </tr>
              </tbody>
            </table>
            {requestDays.some(d => d.noPattern) && src && <div style={{
            fontSize: "0.78em",
            color: "#8C8C8C",
            marginTop: 2
          }}>
                * no working pattern on file, so a 7.5h day is assumed.
              </div>}
            <div style={{
            fontSize: "0.88em",
            marginTop: 8,
            color: "#434343"
          }}>
              <b>Note:</b> {l.reason ? l.reason : <span style={{
              color: "#8C8C8C"
            }}>none given</span>}
            </div>
            {allow && allow.remainingHours != null && src && <div style={{
            fontSize: "0.88em",
            marginTop: 4,
            color: "#434343"
          }}>
                <b>Allowance {keys[0].slice(0, 4)}:</b> {h1(allow.remainingHours)} left after approved leave
                {allow.pendingHours > 0 && ` (${h1(allow.pendingHours)} pending, including this request)`}.
                {" "}Approving this leaves <b style={{
              color: allow.remainingHours - totalOff < 0 ? "#CF1322" : "inherit"
            }}>
                  {h1(allow.remainingHours - totalOff)}</b>
                {allow.allowanceSource === "derived" && <span style={{
              color: "#8C8C8C"
            }}> · allowance derived</span>}.
              </div>}
            {lapsed && <div style={{
            fontSize: "0.85em",
            marginTop: 8,
            padding: "6px 8px",
            background: "#F5F5F5",
            borderRadius: 4,
            color: "#434343"
          }}>
                <b>These dates have already passed.</b> Deciding now only corrects the record — approve if the
                leave was taken (it then counts against the allowance), reject if it wasn&rsquo;t.
              </div>}
          </div>

          {/* ---------------- 2. each day for this role ---------------- */}
          <div style={panel}>
            <div style={panelTitle}>
              2 · What each day looks like for {role} — existing holidays and this request
            </div>
            {!src && !srcErr && <div style={{
            color: "#5D5D5D",
            fontSize: "0.9em"
          }}>Working out cover…</div>}
            {srcErr && <div style={{
            color: "#CF1322",
            fontSize: "0.9em"
          }}>Couldn&rsquo;t load the rota for these dates: {srcErr}</div>}
            {src && <div style={{
            fontSize: "0.8em",
            color: "#5D5D5D",
            marginBottom: 8
          }}>
                Judged on {method === "hours" ? "the hours thresholds (Min / Opt hours)" : method === "staff" ? "the staff thresholds (Min / Opt people)" : `% availability (${target}% acceptable cover)`} for {role}.
                {method === "percent" && tolerance.method !== "percent" && ` ${role} has no thresholds set, so the % rule is used for it.`}
              </div>}
            {judged.map(j => {
            const v = conclusion(j);
            const col = j.mine ? j.vWith ? STATUS[j.vWith] : null : null;
            return <div key={j.key} style={{
              border: "1px solid #F0F0F0",
              borderRadius: 4,
              marginBottom: 10,
              borderLeft: `5px solid ${v.ok ? col?.stripe || "#389E0D" : "#CF1322"}`
            }}>
                  <div style={{
                padding: "6px 10px",
                background: "#FAFAFA",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                gap: 8
              }}>
                    <b>{dayLabel(j.key)}</b>
                    {j.mine && j.vWith && <span style={{
                  fontSize: "0.8em",
                  fontWeight: "bold",
                  color: STATUS[j.vWith].text
                }}>
                        {STATUS[j.vWith].word} · {fmtScore(j.withReq)}
                      </span>}
                  </div>
                  <div style={{
                padding: "6px 10px",
                fontSize: "0.88em",
                color: "#434343"
              }}>
                    {j.on.length === 0 ? <>Nobody in {role} is scheduled this day.</> : explain(j)}
                    {j.mine && j.withoutReq !== null && <div style={{
                  color: "#5D5D5D",
                  marginTop: 2
                }}>
                        Without this request it would be {fmtScore(j.withoutReq)}.
                      </div>}
                    {j.others.length > 0 && <div style={{
                  marginTop: 4,
                  padding: "4px 8px",
                  background: "#FFFBE6",
                  border: "1px solid #FFE58F",
                  borderRadius: 4,
                  color: "#AD4E00"
                }}>
                        This includes {j.others.length} other <b>pending</b> request{j.others.length === 1 ? "" : "s"} not
                        yet decided ({j.others.map(e => displayName(e.staff.name, masked)).join(", ")}).
                        {" "}If {j.others.length === 1 ? "it were" : "they were"} refused, {role} would have{" "}
                        {fmtScore(j.withoutOthers)}{j.vWithoutOthers ? ` (${STATUS[j.vWithoutOthers].word})` : ""}.
                      </div>}
                  </div>
                  {j.all.length > 0 && <table style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "0.84em"
              }}>
                      <thead>
                        <tr style={{
                    color: "#8C8C8C",
                    textAlign: "left"
                  }}>
                          <th style={{
                      padding: "2px 6px",
                      fontWeight: "normal"
                    }}>{role}</th>
                          <th style={{
                      padding: "2px 6px",
                      fontWeight: "normal",
                      textAlign: "right"
                    }}>Expected</th>
                          <th style={{
                      padding: "2px 6px",
                      fontWeight: "normal"
                    }}>On the day</th>
                          <th style={{
                      padding: "2px 6px",
                      fontWeight: "normal",
                      textAlign: "right"
                    }}>Working</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...j.all].sort((a, b) => (a.state === "off") - (b.state === "off") || a.staff.name.localeCompare(b.staff.name)).map(personLine)}
                        <tr style={{
                    borderTop: "1px solid #D9D9D9"
                  }}>
                          <td style={{
                      padding: "3px 6px",
                      color: "#5D5D5D"
                    }}>Total</td>
                          <td style={{
                      padding: "3px 6px",
                      textAlign: "right",
                      color: "#5D5D5D"
                    }}>{h1(j.plannedH)}</td>
                          <td style={{
                      padding: "3px 6px",
                      color: "#5D5D5D"
                    }}>{j.working} of {j.scheduled} working</td>
                          <td style={{
                      padding: "3px 6px",
                      textAlign: "right",
                      fontWeight: "bold"
                    }}>{h1(j.avail)}</td>
                        </tr>
                      </tbody>
                    </table>}
                  <div style={{
                padding: "6px 10px",
                fontSize: "0.88em",
                fontWeight: "bold",
                color: v.ok ? "#15803d" : "#CF1322",
                background: v.ok ? "#F6FFED" : "#FFF1F0"
              }}>
                    {v.ok ? "✓ " : "✗ "}{v.text}
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* ---------------- 3. decision ---------------- */}
        <div style={{
        padding: "10px 16px",
        borderTop: "1px solid #F0F0F0",
        display: "flex",
        alignItems: "center",
        gap: 10,
        flexWrap: "wrap"
      }}>
          <div style={{
          flex: 1,
          fontSize: "0.9em",
          fontWeight: "bold",
          color: !src ? "#5D5D5D" : bad.length ? "#CF1322" : "#15803d"
        }}>
            {!src ? "Working out cover…" : bad.length ? `✗ Not acceptable on ${bad.length} of ${judged.length} day${judged.length === 1 ? "" : "s"}: ${bad.map(j => dayLabel(j.key)).join(", ")}` : `✓ Acceptable on ${judged.length === 1 ? "this day" : `all ${judged.length} days`}`}
          </div>
          <button onClick={onClose} style={{
          background: "#fff",
          color: "#262626",
          border: "1px solid #D9D9D9",
          borderRadius: 4,
          padding: "6px 12px",
          cursor: "pointer"
        }}>Cancel</button>
          <button onClick={() => decide("REJECTED")} disabled={busy} style={{
          background: "#CF1322",
          color: "#fff",
          border: 0,
          borderRadius: 4,
          padding: "6px 12px",
          cursor: "pointer",
          opacity: busy ? 0.6 : 1
        }}>Reject</button>
          <button onClick={() => decide("APPROVED")} disabled={busy} style={{
          background: "#389E0D",
          color: "#fff",
          border: 0,
          borderRadius: 4,
          padding: "6px 12px",
          cursor: "pointer",
          opacity: busy ? 0.6 : 1
        }}>Approve</button>
        </div>
      </div>
    </div>;
}
